import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { brand } from "../../theme/theme";
import { useOutletContext, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Divider,
  Grid,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import {
  Mail,
  Phone,
  Plus,
  X,
  ChevronDown,
  ChevronUp,
  BellOff,
  Edit,
  Save,
  Link2,
  Trash2,
  Copy,
  Check,
  Bot,
  Sparkles,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { ADAPTATION_PROMPTS, EMPTY_PROMPT_PLACEHOLDER, REPORT_PROMPTS } from "../../components/prompts/agencyPrompts";
import { aiConfigEndpoint, aiConfigQueryKey, updateAiConfig } from "../../services/contentGenerationService";
import { reportAiConfigEndpoint, reportAiConfigQueryKey, updateReportAiConfig } from "../../services/reportsService";
import { PromptField } from "../../components/prompts/PromptField";
import { Card, CardContent } from "../../components/Card";
import { Button } from "../../components/Button";
import { Badge } from "../../components/Badge";
import { Checkbox } from "../../components/Checkbox";
import { CustomTextField } from "../../components/CustomTextField";
import { Label } from "../../components/Label";
import { Avatar, AvatarFallback } from "../../components/Avatar";
import { UnifiedModal } from "../../components/UnifiedModal";
import { ConfirmModal } from "../../components/ConfirmModal";
import { DataTable } from "../../components/DataTable";
import { ToggleSwitch } from "../../components/ToggleSwitch";
import { CustomSelector } from "@/components/CustomSelector";
import { TimeLogCard } from "../../components/TimeLogCard";
import { FEATURE_HOUR_PACKAGE, hasAgencyFeature } from "../../utils/agencyFeatures";
import { useAuth } from "../../components/contexts/AuthContext";
import useCustomTheme from "../../theme/useTheme";
import { getData } from "../../components/hooks/getData";
import apiClient from "../../api-client";
import {
  connectSeRankingAccount,
  disconnectSeRankingAccount,
  listConnectableUsers,
  listSeRankingAccounts,
  listSeRankingSubaccounts,
} from "../../services/seRankingService";
import {
  clientifyErrorMessage,
  connectClientifyUser,
  disconnectClientifyUser,
  listClientifyConnectableUsers,
  listClientifyConnections,
  listClientifyUsers,
} from "../../services/clientifyService";
import SuccessPopup from "../../components/SuccessPopup";
import { CsvExportsTab } from "./CsvExportsTab";
import { AgencyBrandingSettings, BrandPreview } from "../../components/AgencyBrandingSettings";
import UnsavedChangesBar from "../../components/UnsavedChangesBar";
import { usePasteSingleImage } from "../../components/hooks/usePasteImage";
import { max } from "date-fns";
import { use } from "react";

// Mirrors BEHAVIOR_PROMPT_MAX_LENGTH in markezy-backend/copilot/models.py
const COPILOT_BEHAVIOR_PROMPT_MAX_LENGTH = 4000;

const NOTIFICATION_SUBTABS = [
  { value: "channels", label: "Canales de notificación", icon: Mail },
  { value: "muted", label: "Silenciadas", icon: BellOff },
];

const ROLE_LABELS = {
  superadmin: "Super Administrador",
  agency_admin: "Administrador de agencia",
  consultant: "Consultor",
  client: "Cliente",
  freelance_ticket: "Freelance tickets",
};

const NOTIFICATION_TYPES = [
  {
    id: "unread_in_thread",
    title: "Mensajes sin leer",
    description:
      "Te avisamos cuando hay actividad pendiente en conversaciones que sigues.",
  },
  {
    id: "ticket_created",
    title: "Ticket creado",
    description: "Se abrió un nuevo ticket que impacta a tu equipo o clientes.",
  },
  {
    id: "ticket_assigned",
    title: "Ticket asignado",
    description: "Te asignaron un ticket para gestionar.",
  },
  {
    id: "ticket_status_changed",
    title: "Estado del ticket",
    description: "El estado de un ticket cambia a nuevo, abierto o cerrado.",
  },
  {
    id: "ticket_hours_redeemed",
    title: "Horas consumidas",
    description: "Un ticket ha consumido horas de bolsa y necesitas revisarlo.",
  },
  {
    id: "central_announcement",
    title: "Anuncio general",
    description:
      "Serseo comparte anuncios importantes para toda la organización.",
  },
  {
    id: "user_created",
    title: "Nuevo usuario",
    description:
      "Se invitó a un nuevo usuario a la plataforma dentro de tu ámbito.",
  },
  {
    id: "client_created",
    title: "Cliente creado",
    description: "Se dio de alta un nuevo cliente en una agencia.",
  },
  {
    id: "client_info_updated",
    title: "Actualización de cliente",
    description: "Un cliente actualizó sus datos o cambió de estado.",
  },
  {
    id: "client_onboarding_completed",
    title: "Onboarding completado",
    description: "Un cliente completó el proceso de onboarding.",
  },
  {
    id: "conversation_mention",
    title: "Menciones",
    description: "Alguien te mencionó dentro de una conversación.",
  },
  {
    id: "conversation_important",
    title: "Marcado como importante",
    description: "Un mensaje se marcó como importante en tus conversaciones.",
  },
  {
    id: "conversation_task_assigned",
    title: "Tarea dentro de conversación",
    description: "Te asignaron una acción desde una conversación.",
  },
  {
    id: "task_assigned",
    title: "Tarea asignada",
    description: "Recibiste una nueva tarea o seguimiento.",
  },
  {
    id: "task_deadline",
    title: "Fecha límite",
    description: "Una tarea se acerca a su fecha límite comprometida.",
  },
  {
    id: "project_phase_completed",
    title: "Fase de proyecto completada",
    description: "Una fase del proyecto finalizó y requiere tu confirmación.",
  },
  {
    id: "content_review",
    title: "Contenido para revisar",
    description: "Hay contenido nuevo esperando tu aprobación.",
  },
  {
    id: "content_updated",
    title: "Contenido actualizado",
    description: "Se modificó contenido que tienes pendiente de revisar.",
  },
  {
    id: "report_available",
    title: "Informe disponible",
    description: "Tu informe ya está disponible.",
  },
];

const CHANNELS = [
  {
    field: "show_in_dashboard",
    label: "Dashboard",
  },
  {
    field: "send_email",
    label: "Email",
  },
];

const ROLE_NOTIFICATION_RULES = {
  // Derived from backend notification matrix: dictates which types each role may configure
  superadmin: [
    "unread_in_thread",
    "conversation_mention",
    "conversation_important",
    "conversation_task_assigned",
    "ticket_created",
    "ticket_assigned",
    "user_created",
    "client_created",
  ],
  agency_admin: [
    "unread_in_thread",
    "conversation_mention",
    "conversation_important",
    "conversation_task_assigned",
    "task_assigned",
    "task_deadline",
    "ticket_created",
    "ticket_status_changed",
    "ticket_hours_redeemed",
    "user_created",
    "client_info_updated",
    "client_onboarding_completed",
    "central_announcement",
  ],
  consultant: [
    "unread_in_thread",
    "conversation_mention",
    "conversation_important",
    "conversation_task_assigned",
    "task_assigned",
    "task_deadline",
    "ticket_created",
    "ticket_status_changed",
    "ticket_hours_redeemed",
    "user_created",
    "client_info_updated",
    "client_onboarding_completed",
    "central_announcement",
  ],
  freelance_ticket: [
    "unread_in_thread",
    "conversation_mention",
    "ticket_assigned",
    "ticket_status_changed",
  ],
  client: [
    "unread_in_thread",
    "conversation_mention",
    "conversation_important",
    "conversation_task_assigned",
    "project_phase_completed",
    "content_review",
    "content_updated",
    "report_available",
    "central_announcement",
  ],
};

const buildDefaultPreferences = () =>
  NOTIFICATION_TYPES.reduce((acc, type) => {
    acc[type.id] = {
      id: null,
      notification_type: type.id,
      show_in_dashboard: false,
      send_email: false,
      is_active: true,
    };
    return acc;
  }, {});

const normalizeApiList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

/**
 * "Ana Pérez" — same fallback chain the consultant pickers use elsewhere (see
 * `Clients.jsx`), so the same person reads the same way across the app.
 */
const userDisplayName = (user) =>
  user?.full_name ||
  user?.name ||
  `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
  user?.email ||
  (user?.id ? `Usuario ${user.id}` : "Sin nombre");

/**
 * Agency of a user, by name. The user serializer sends `agency` as a bare id,
 * so it is resolved against the agencies already loaded on this page; an
 * `agency_name` or a nested object, if the backend sends one, wins over it.
 */
const userAgencyName = (user, agencyNamesById) => {
  if (!user) return "";
  if (user.agency_name) return user.agency_name;
  if (user.agency && typeof user.agency === "object") {
    return user.agency.name || "";
  }
  if (user.agency === null || user.agency === undefined) return "";
  return agencyNamesById?.[String(user.agency)] || "";
};

/**
 * "Ana Pérez - Agencia Norte" — the agency is part of the label because a
 * superadmin picking a sub-account owner needs to tell apart two homonyms
 * from different agencies.
 */
const serankingUserLabel = (user, agencyNamesById) => {
  const agencyName = userAgencyName(user, agencyNamesById);
  return [userDisplayName(user), agencyName].filter(Boolean).join(" - ");
};

// Configuración de tamaño de letra para conversaciones (en píxeles)
const FONT_SIZE_MIN = 10;
const FONT_SIZE_MAX = 16;
const FONT_SIZE_DEFAULT = 12;

const getFilterButtonStyles = (isSelected, theme, neutrals, colors) => ({
  borderRadius: "999px",
  px: 1.5,
  py: 0.5,
  fontSize: "0.8125rem",
  minHeight: "32px",
  backgroundColor: isSelected ? neutrals.gray[200] : "transparent",
  color: isSelected ? neutrals.gray[700] : neutrals.gray[500],
  border: "none",
  fontWeight: 500,
  "&:hover": {
    backgroundColor: isSelected ? neutrals.gray[50] : "transparent",
  },
});

function PromptEditor({ entry, theme, neutrals, onDirtyChange }) {
  const Icon = entry.Icon;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: { xs: 2, md: 3 },
          py: 2,
          minWidth: 0,
          borderBottom: `1px solid ${neutrals.gray[100]}`,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            flexShrink: 0,
            backgroundColor: `${entry.color || theme.palette.primary.main}1A`,
            color: entry.color || theme.palette.primary.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={18} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {entry.label}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
            {entry.description}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 }, py: 2, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <PromptField
          id={`prompt-${entry.id}`}
          ariaLabel={`Prompt de ${entry.label}`}
          value={entry.value || ""}
          onSave={entry.onSave}
          onDirtyChange={onDirtyChange}
          placeholder={entry.placeholder}
          showCounts
          floatingSave
          textareaSx={{ flex: 1, minHeight: "380px" }}
        />
      </Box>
    </Box>
  );
}

PromptEditor.propTypes = {
  entry: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    description: PropTypes.string,
    value: PropTypes.string,
    onSave: PropTypes.func,
    placeholder: PropTypes.string,
    color: PropTypes.string,
    Icon: PropTypes.elementType.isRequired,
  }).isRequired,
  theme: PropTypes.object.isRequired,
  neutrals: PropTypes.object.isRequired,
  onDirtyChange: PropTypes.func.isRequired,
};

function PromptsWorkspace({ groups, theme, neutrals, initialSelectedId }) {
  const { colors } = useCustomTheme();
  const entries = groups.flatMap((group) => group.entries);
  const [selectedId, setSelectedId] = useState(() =>
    entries.some((entry) => entry.id === initialSelectedId) ? initialSelectedId : entries[0]?.id
  );
  const [dirty, setDirty] = useState(false);
  const [pendingId, setPendingId] = useState(null);
  const selected = entries.find((entry) => entry.id === selectedId) || entries[0];

  const handleSelect = (id) => {
    if (id === selected.id) return;
    if (dirty) {
      setPendingId(id);
      return;
    }
    setSelectedId(id);
  };

  const discardAndSelect = () => {
    setDirty(false);
    setSelectedId(pendingId);
    setPendingId(null);
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "250px 1fr" },
        border: `1px solid ${neutrals.gray[200]}`,
        borderRadius: "12px",
        overflow: "hidden",
        minHeight: 520,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          borderRight: { md: `1px solid ${neutrals.gray[100]}` },
          borderBottom: { xs: `1px solid ${neutrals.gray[100]}`, md: "none" },
          // Mismas entradas que el menú principal y el de Ajustes
          padding: "0 12px 12px 8px",
          display: "flex",
          flexDirection: "column",
          maxHeight: { xs: 280, md: "none" },
          overflowY: "auto",
        }}
      >
        {groups.map((group) => (
          <Box key={group.title} sx={{ display: "flex", flexDirection: "column" }}>
            <Typography
              sx={{
                padding: "14px 12px 4px 12px",
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: theme.palette.text.primary,
                lineHeight: 1.2,
              }}
            >
              {group.title}
            </Typography>
            {group.entries.map((entry) => {
              const active = entry.id === selected.id;
              const Icon = entry.Icon;
              return (
                <Box
                  key={entry.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelect(entry.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") handleSelect(entry.id);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    height: 34,
                    flexShrink: 0,
                    px: "16px",
                    mb: "2px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    backgroundColor: active ? colors.secondary.main : "transparent",
                    "&:hover": { backgroundColor: active ? colors.secondary.main : neutrals.gray[50] },
                  }}
                >
                  {/* Los iconos de plataforma conservan su color de marca */}
                  <Icon size={16} color={entry.color || (active ? colors.brand.pink : colors.nav.icon)} />
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontWeight: active ? 600 : 400,
                      fontSize: "14px",
                      color: active ? colors.brand.pink : colors.nav.text,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {entry.label}
                  </Typography>
                  {entry.status === "loading" ? (
                    <CircularProgress size={12} />
                  ) : null}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      {selected.status === "ready" ? (
        <PromptEditor
          key={selected.editorKey || selected.id}
          entry={selected}
          theme={theme}
          neutrals={neutrals}
          onDirtyChange={setDirty}
        />
      ) : (
        <Box sx={{ p: 4, color: neutrals.gray[500] }}>
          <Typography variant="body2">
            {selected.status === "loading" ? "Cargando..." : selected.message}
          </Typography>
        </Box>
      )}

      <UnifiedModal
        open={pendingId !== null}
        onClose={() => setPendingId(null)}
        title="Cambios sin guardar"
        maxWidth="xs"
      >
        <Typography variant="body2" sx={{ color: neutrals.gray[600], mb: 3 }}>
          Has editado este prompt y no has guardado los cambios. Si cambias de prompt, se perderán.
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <Button variant="outline" onClick={() => setPendingId(null)}>
            Seguir editando
          </Button>
          <Button variant="destructive" onClick={discardAndSelect}>
            Descartar cambios
          </Button>
        </Box>
      </UnifiedModal>
    </Box>
  );
}

PromptsWorkspace.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.object).isRequired,
  theme: PropTypes.object.isRequired,
  neutrals: PropTypes.object.isRequired,
  initialSelectedId: PropTypes.string,
};

// `section`: pestaña a mostrar; la navegación y la visibilidad de cada una viven en Pages/Settings
export function UserSettings({ userData, section }) {
  const { user, interceptorReady, currentUser, refreshUser } = useAuth();
  const { setPageContext, userRefreshKey, updateUiPreferences } = useOutletContext();
  const muiTheme = useMuiTheme();
  const { colors, neutrals } = useCustomTheme();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const activeTab = section;
  const [activeNotificationSubtab, setActiveNotificationSubtab] = useState(
    NOTIFICATION_SUBTABS[0].value
  );
  const [preferences, setPreferences] = useState(buildDefaultPreferences);
  const [loadingPreferences, setLoadingPreferences] = useState(true);
  const [preferencesError, setPreferencesError] = useState(null);
  const [preferencesVersion, setPreferencesVersion] = useState(0);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupError, setPopupError] = useState(false);
  const [popupDuration, setPopupDuration] = useState(3000);
  const [mutedSourcesError, setMutedSourcesError] = useState(null);
  const [availableAgencies, setAvailableAgencies] = useState([]);
  const [availableClients, setAvailableClients] = useState([]);
  const [mutedAgencies, setMutedAgencies] = useState([]);
  const [mutedClients, setMutedClients] = useState([]);
  const [muteProcessingMap, setMuteProcessingMap] = useState({});
  const [selectedAgencyFilter, setSelectedAgencyFilter] = useState(null);
  const [selectedClientFilter, setSelectedClientFilter] = useState(null);
  const [clientSelectorValue, setClientSelectorValue] = useState("__all__");
  const [mutedRefreshKey, setMutedRefreshKey] = useState(0);
  const [podioConnections, setPodioConnections] = useState([]);
  const [podioConnectionsLoading, setPodioConnectionsLoading] = useState(false);
  const [podioConnectionsError, setPodioConnectionsError] = useState(null);
  const [podioSpaces, setPodioSpaces] = useState([]);
  const [podioSpacesLoading, setPodioSpacesLoading] = useState(false);
  const [podioSpacesError, setPodioSpacesError] = useState(null);
  const [podioSpacesLoaded, setPodioSpacesLoaded] = useState(false);
  const [podioRateLimit, setPodioRateLimit] = useState(null);
  const [podioSelectedClient, setPodioSelectedClient] = useState(null);
  const [podioSelectedSpace, setPodioSelectedSpace] = useState(null);
  const [podioRefreshKey, setPodioRefreshKey] = useState(0);
  const [promptsRefreshKey, setPromptsRefreshKey] = useState(0);

  const [creatingPodioConnection, setCreatingPodioConnection] = useState(false);
  const [syncingConnectionId, setSyncingConnectionId] = useState(null);
  const [expandedSyncErrors, setExpandedSyncErrors] = useState({});

  // Clientify connection state. Personal, like SE Ranking: one
  // agency_admin/consultant ↔ one Clientify user, addressed by the Markezy
  // user id since the connection is a field on the user.
  const [clientifyUsers, setClientifyUsers] = useState([]);
  const [clientifyUsersLoading, setClientifyUsersLoading] = useState(false);
  const [clientifyUsersError, setClientifyUsersError] = useState(null);
  const [usersWithoutClientify, setUsersWithoutClientify] = useState([]);
  const [usersWithoutClientifyLoading, setUsersWithoutClientifyLoading] = useState(false);
  const [clientifyConnections, setClientifyConnections] = useState([]);
  const [clientifyConnectionsLoading, setClientifyConnectionsLoading] = useState(false);
  const [selectedUserForClientify, setSelectedUserForClientify] = useState(null);
  const [selectedClientifyUser, setSelectedClientifyUser] = useState(null);
  const [connectingClientifyUser, setConnectingClientifyUser] = useState(false);
  const [showDeleteConnectionModal, setShowDeleteConnectionModal] = useState(false);
  const [connectionToDelete, setConnectionToDelete] = useState(null);
  const [deletingConnection, setDeletingConnection] = useState(false);
  // SE Ranking connection state. Same two lists as Clientify (sub-accounts to
  // pick from, plus who is still free to connect) except the link is personal:
  // it binds one agency_admin/consultant to one sub-account, not an agency, so
  // each connection comes back as a flat row with its own id (the one DELETE is
  // addressed with) plus user_* / seranking_* fields.
  const [serankingSubaccounts, setSerankingSubaccounts] = useState([]);
  const [serankingSubaccountsLoading, setSerankingSubaccountsLoading] =
    useState(false);
  const [serankingSubaccountsError, setSerankingSubaccountsError] =
    useState(null);
  const [usersWithoutSeranking, setUsersWithoutSeranking] = useState([]);
  const [usersWithoutSerankingLoading, setUsersWithoutSerankingLoading] =
    useState(false);
  const [serankingConnections, setSerankingConnections] = useState([]);
  const [serankingConnectionsLoading, setSerankingConnectionsLoading] =
    useState(false);
  const [selectedUserForSeranking, setSelectedUserForSeranking] =
    useState(null);
  const [selectedSerankingSubaccount, setSelectedSerankingSubaccount] =
    useState(null);
  const [connectingSerankingUser, setConnectingSerankingUser] =
    useState(false);
  const [showDeleteSerankingModal, setShowDeleteSerankingModal] =
    useState(false);
  const [serankingConnectionToDelete, setSerankingConnectionToDelete] =
    useState(null);
  const [deletingSerankingConnection, setDeletingSerankingConnection] =
    useState(false);

  const [ticketFreelancersRefreshKey, setTicketFreelancersRefreshKey] = useState(0);
  const [generatedTicketFreelanceInvite, setGeneratedTicketFreelanceInvite] = useState(null);
  const [generatingTicketFreelanceInvite, setGeneratingTicketFreelanceInvite] = useState(false);
  const [freelancerInviteCopied, setFreelancerInviteCopied] = useState(false);

  // Edit photo modal
  const [showEditPhotoModal, setShowEditPhotoModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [photoError, setPhotoError] = useState(null);
  const [cropMessage, setCropMessage] = useState(null);
  const [imageMarkedForDeletion, setImageMarkedForDeletion] = useState(false);
  const fileInputRef = useRef(null);

  // Form data
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    telephone: "",
  });
  const [originalFormData, setOriginalFormData] = useState({
    first_name: "",
    last_name: "",
    telephone: "",
  });
  const [fieldErrors, setFieldErrors] = useState({
    first_name: false,
    last_name: false,
    telephone: false,
  });

  // Appearance preferences
  const [conversationFontSize, setConversationFontSize] = useState(
    () => {
      const savedSize = user?.ui_preferences?.conversation_font_size;
      // Si es un número válido, usarlo; si no, usar el default
      if (typeof savedSize === 'number' && savedSize >= FONT_SIZE_MIN && savedSize <= FONT_SIZE_MAX) {
        return savedSize;
      }
      return FONT_SIZE_DEFAULT;
    }
  );
  const [fontSizeInput, setFontSizeInput] = useState(() => {
    const savedSize = user?.ui_preferences?.conversation_font_size;
    if (typeof savedSize === "number" && savedSize >= FONT_SIZE_MIN && savedSize <= FONT_SIZE_MAX) {
      return String(savedSize);
    }
    return String(FONT_SIZE_DEFAULT);
  });
  const [originalConversationFontSize, setOriginalConversationFontSize] = useState(
    () => {
      const savedSize = user?.ui_preferences?.conversation_font_size;
      if (typeof savedSize === 'number' && savedSize >= FONT_SIZE_MIN && savedSize <= FONT_SIZE_MAX) {
        return savedSize;
      }
      return FONT_SIZE_DEFAULT;
    }
  );

  // Use userData from props if available, otherwise fetch from API
  const { data: profileData, error: profileError } = userData
    ? { data: userData, error: null }
    : getData("users/me/", interceptorReady, [userRefreshKey]);

  // Fetch notification preferences
  const { data: preferencesData, error: preferencesDataError } = getData(
    "dashboard/notification-preferences/",
    interceptorReady,
    [preferencesVersion]
  );

  const loadingProfile = !profileData && !profileError;

  // Sync form data with profile data
  React.useEffect(() => {
    if (profileData) {
      const newFormData = {
        first_name: profileData.first_name || "",
        last_name: profileData.last_name || "",
        telephone: profileData.telephone || "",
      };
      setFormData(newFormData);
      setOriginalFormData(newFormData);
    }
  }, [profileData]);

  // Sync appearance preferences with user data
  React.useEffect(() => {
    const savedSize = user?.ui_preferences?.conversation_font_size;
    if (typeof savedSize === 'number' && savedSize >= FONT_SIZE_MIN && savedSize <= FONT_SIZE_MAX) {
      setConversationFontSize(savedSize);
      setOriginalConversationFontSize(savedSize);
      setFontSizeInput(String(savedSize));
    }
  }, [user?.ui_preferences?.conversation_font_size]);

  const profile = profileData || {
    email: user?.email || "",
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    telephone: user?.telephone || null,
    role: user?.role || "",
    profile_image: user?.profile_image || null,
  };

  const userRole = profile.role || user?.role || "client";
  const availableNotificationTypes = React.useMemo(() => {
    const allowedTypeIds =
      ROLE_NOTIFICATION_RULES[userRole] ||
      NOTIFICATION_TYPES.map((type) => type.id);
    return NOTIFICATION_TYPES.filter((type) =>
      allowedTypeIds.includes(type.id)
    );
  }, [userRole]);

  const initials = React.useMemo(() => {
    const chars = [profile.first_name, profile.last_name]
      .filter(Boolean)
      .map((name) => name.charAt(0).toUpperCase());
    if (chars.length === 0 && profile.email) {
      return profile.email.charAt(0).toUpperCase();
    }
    return chars.slice(0, 2).join("") || "U";
  }, [profile]);

  const canViewMutedSources =
    userRole === "superadmin" ||
    userRole === "agency_admin" ||
    userRole === "consultant";
  const canToggleAgencyMutes = userRole === "superadmin";
  const canToggleClientMutes =
    userRole === "agency_admin" || userRole === "consultant";
  const notificationSubtabsForUser = React.useMemo(
    () =>
      canViewMutedSources
        ? NOTIFICATION_SUBTABS
        : NOTIFICATION_SUBTABS.filter((subtab) => subtab.value === "channels"),
    [canViewMutedSources]
  );

  const clientifyUserLabelById = React.useMemo(() => {
    const map = new Map();
    clientifyUsers.forEach((clientifyUser) => {
      map.set(
        String(clientifyUser.clientify_user_id),
        [clientifyUser.full_name, clientifyUser.email].filter(Boolean).join(" - ")
      );
    });
    return map;
  }, [clientifyUsers]);

  const clientsEndpoint = React.useMemo(() => {
    if (userRole === "agency_admin" || userRole === "consultant") {
      return `clients/options/?ordering=name`; // Backend filters by user's access automatically
    }
    return null;
  }, [userRole]);

  const shouldFetchPromptConfigs =
    interceptorReady && userRole === "superadmin" && activeTab === "prompts";
  // Content prompts are per agency: only its admin edits them.
  const shouldFetchContentPrompts =
    interceptorReady && userRole === "agency_admin" && activeTab === "prompts";
  // Report prompts are per agency too: the agency admin edits their own, the superadmin picks one.
  const [reportPromptsAgencyId, setReportPromptsAgencyId] = useState(null);
  const shouldFetchReportPrompts =
    interceptorReady &&
    activeTab === "prompts" &&
    (userRole === "agency_admin" || (userRole === "superadmin" && Boolean(reportPromptsAgencyId)));
  const { data: promptAgenciesData } = getData("agencies/", shouldFetchPromptConfigs, 0);
  const shouldFetchTicketFreelancers =
    interceptorReady &&
    userRole === "superadmin" &&
    activeTab === "ticket-freelancers";
  const shouldFetchCopilotConfig =
    interceptorReady &&
    userRole === "agency_admin" &&
    activeTab === "prompts";
  const [copilotConfigRefreshKey, setCopilotConfigRefreshKey] = useState(0);
  const agencyIdForHourPackage = currentUser?.agencyId || user?.agencyId || null;
  const shouldFetchAgencyHourPackage =
    interceptorReady &&
    activeTab === "hour-package" &&
    Boolean(agencyIdForHourPackage);

  const {
    data: copilotConfigData,
    error: copilotConfigError,
    isLoading: isLoadingCopilotConfig,
  } = getData(
    shouldFetchCopilotConfig ? "copilot/agency-config/" : null,
    shouldFetchCopilotConfig,
    copilotConfigRefreshKey
  );

  const {
    data: marketingPromptData,
    error: marketingPromptError,
    isLoading: isLoadingMarketingPrompt,
  } = getData(
    shouldFetchPromptConfigs ? "/leadmagnet/strategy/system-prompt/" : null,
    shouldFetchPromptConfigs,
    promptsRefreshKey
  );

  const {
    data: contentPromptData,
    error: contentPromptError,
    isLoading: isLoadingContentPrompts,
  } = getData(
    shouldFetchContentPrompts ? aiConfigEndpoint() : null,
    shouldFetchContentPrompts,
    0 // Shared cache entry with "Configurar prompts" (aiConfigQueryKey), updated in place on save.
  );

  const {
    data: reportPromptData,
    error: reportPromptError,
    isLoading: isLoadingReportPrompts,
  } = getData(
    reportAiConfigEndpoint(reportPromptsAgencyId),
    shouldFetchReportPrompts,
    0 // Shared cache entry with the reports "Configurar prompts" (reportAiConfigQueryKey), updated in place on save.
  );
  const { data: ticketFreelancersData, isLoading: ticketFreelancersLoading } =
    getData(
      shouldFetchTicketFreelancers ? "/users/?role=freelance_ticket" : null,
      shouldFetchTicketFreelancers,
      [ticketFreelancersRefreshKey]
    );
  const {
    data: agencyHourPackageData,
    isLoading: isLoadingAgencyHourPackage,
    error: agencyHourPackageError,
  } = getData(
    shouldFetchAgencyHourPackage ? `agencies/${agencyIdForHourPackage}/` : null,
    shouldFetchAgencyHourPackage,
    [agencyIdForHourPackage]
  );
  const agencyHourPackageBalance = shouldFetchAgencyHourPackage ? agencyHourPackageData?.hour_package ?? null : null;

  const contentPromptConfig = React.useMemo(() => {
    if (!contentPromptData) return null;
    if (Array.isArray(contentPromptData?.results)) {
      return contentPromptData.results[0] || null;
    }
    if (Array.isArray(contentPromptData)) {
      return contentPromptData[0] || null;
    }
    if (contentPromptData && typeof contentPromptData === "object") {
      return contentPromptData;
    }
    return null;
  }, [contentPromptData]);

  // Título y breadcrumb los pone Ajustes (Pages/Settings)
  React.useEffect(() => {
    setPageContext({});
  }, [setPageContext]);

  React.useEffect(() => {
    if (activeTab === "prompts") {
      setPromptsRefreshKey((prev) => prev + 1);
    }
  }, [activeTab]);

  React.useEffect(() => {
    if (
      selectedAgencyFilter &&
      !availableAgencies.some((agency) => agency.id === selectedAgencyFilter)
    ) {
      setSelectedAgencyFilter(null);
    }
  }, [availableAgencies, selectedAgencyFilter]);

  React.useEffect(() => {
    if (
      selectedClientFilter &&
      !availableClients.some((client) => client.id === selectedClientFilter)
    ) {
      setSelectedClientFilter(null);
    }
  }, [availableClients, selectedClientFilter]);

  // Process notification preferences data
  React.useEffect(() => {
    if (!preferencesData && !preferencesDataError) {
      return;
    }

    const defaultPreferences = buildDefaultPreferences();

    if (preferencesDataError) {
      console.error(
        "Error loading notification preferences:",
        preferencesDataError
      );
      const errorMessage =
        preferencesDataError.response?.data?.detail ||
        "No pudimos cargar tus preferencias. Intenta nuevamente.";
      setPreferencesError(errorMessage);
      setLoadingPreferences(false);
      return;
    }

    if (preferencesData) {
      const responseData = (() => {
        if (Array.isArray(preferencesData)) return preferencesData;
        if (Array.isArray(preferencesData?.results))
          return preferencesData.results;
        if (preferencesData && typeof preferencesData === "object") {
          return Object.values(preferencesData);
        }
        return [];
      })();

      responseData.forEach((preference) => {
        if (
          preference?.notification_type &&
          defaultPreferences[preference.notification_type]
        ) {
          defaultPreferences[preference.notification_type] = {
            ...defaultPreferences[preference.notification_type],
            ...preference,
          };
        }
      });

      setPreferences(defaultPreferences);
      setPreferencesError(null);
      setLoadingPreferences(false);
    }
  }, [preferencesData, preferencesDataError]);

  const handlePreferenceChange = async (typeId, field, checked) => {
    const preferenceRecord = preferences[typeId];
    if (!preferenceRecord) return;

    const preferenceIdentifier =
      preferenceRecord.id || preferenceRecord.notification_type || typeId;
    const normalizedValue = Boolean(checked);

    if (loadingPreferences || preferenceRecord[field] === normalizedValue) {
      return;
    }

    const previousValue = preferenceRecord[field];

    // Actualización optimista local
    setPreferences((prev) => ({
      ...prev,
      [typeId]: {
        ...prev[typeId],
        [field]: normalizedValue,
      },
    }));

    try {
      await apiClient.patch(
        `dashboard/notification-preferences/${preferenceIdentifier}/`,
        {
          [field]: normalizedValue,
        }
      );
      // Éxito: mantener actualización local, no hacer refresh
      setPopupMessage("Preferencia de notificación actualizada");
      setPopupError(false);
      setPopupDuration(1000);
      setShowSuccessPopup(true);
    } catch (error) {
      console.error("Error updating notification preference:", error);
      // Error: revertir cambio local y hacer refresh
      setPreferences((prev) => ({
        ...prev,
        [typeId]: {
          ...prev[typeId],
          [field]: previousValue,
        },
      }));
      const errorMessage =
        error.response?.data?.[field]?.[0] ||
        error.response?.data?.detail ||
        "No pudimos guardar este cambio.";
      setPopupMessage(errorMessage);
      setPopupError(true);
      setPopupDuration(1000);
      setShowSuccessPopup(true);
      // Hacer refresh solo si falla
      setPreferencesVersion((prev) => prev + 1);
    }
  };

  const handleReloadPreferences = () => {
    setLoadingPreferences(true);
    setPreferencesError(null);
    setPreferencesVersion((prev) => prev + 1);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: false }));
  };

  const [savingProfile, setSavingProfile] = useState(false);
  const profileChanges = Object.keys(formData).filter((field) => formData[field] !== originalFormData[field]).length;

  const discardProfile = () => {
    setFormData(originalFormData);
    setFieldErrors({ first_name: false, last_name: false, telephone: false });
  };

  const handleSave = async () => {
    const emptyFields = Object.fromEntries(Object.keys(formData).map((field) => [field, !formData[field].trim()]));
    if (Object.values(emptyFields).some(Boolean)) {
      setFieldErrors(emptyFields);
      return;
    }

    setSavingProfile(true);
    try {
      await apiClient.patch("users/me/", formData);
      setOriginalFormData(formData);
      // Refresh user data in global context
      await refreshUser();
      setPopupMessage("Perfil actualizado exitosamente");
      setPopupError(false);
      setPopupDuration(3000);
      setShowSuccessPopup(true);
    } catch (error) {
      console.error("Error actualizando perfil:", error);
      setPopupMessage(
        error.response?.data?.message || "Error al actualizar el perfil"
      );
      setPopupError(true);
      setPopupDuration(3000);
      setShowSuccessPopup(true);
    } finally {
      setSavingProfile(false);
    }
  };

  const fontSizeDirty = conversationFontSize !== originalConversationFontSize;

  const [savingFontSize, setSavingFontSize] = useState(false);

  const saveFontSize = async () => {
    const safeValue = Math.min(conversationFontSize, FONT_SIZE_MAX);
    setConversationFontSize(safeValue);
    setFontSizeInput(String(safeValue));
    setSavingFontSize(true);
    await handleAppearanceChange("conversation_font_size", safeValue);
    setSavingFontSize(false);
  };

  const discardFontSize = () => {
    setConversationFontSize(originalConversationFontSize);
    setFontSizeInput(String(originalConversationFontSize));
  };

  // Appearance preferences handler
  const handleAppearanceChange = async (key, value) => {
    try {
      await updateUiPreferences({
        [key]: value,
      });

      // Actualizar el valor original después de guardar exitosamente
      if (key === "conversation_font_size") {
        setOriginalConversationFontSize(value);
      }

      setPopupMessage("Preferencia de apariencia actualizada");
      setPopupError(false);
      setPopupDuration(2000);
      setShowSuccessPopup(true);
    } catch (error) {
      console.error("Error actualizando preferencias de apariencia:", error);
      setPopupMessage(
        error.response?.data?.message || "Error al actualizar la preferencia"
      );
      setPopupError(true);
      setPopupDuration(3000);
      setShowSuccessPopup(true);
    }
  };

  const parseFontSizeInput = (value) => {
    const normalized = value.trim();
    if (!normalized) {
      return { valid: false, value: FONT_SIZE_DEFAULT };
    }
    const parsed = Number.parseFloat(normalized);
    if (Number.isNaN(parsed)) {
      return { valid: false, value: FONT_SIZE_DEFAULT };
    }
    // Clamp entre 10 y 16
    const clamped = Math.max(FONT_SIZE_MIN, Math.min(FONT_SIZE_MAX, parsed));
    return { valid: true, value: clamped };
  };

  const fontSizeValidation = React.useMemo(
    () => parseFontSizeInput(fontSizeInput),
    [fontSizeInput]
  );

  // Photo management functions
  const handleEditPhoto = () => {
    setImageMarkedForDeletion(false);
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setPhotoError(null);
    setCropMessage(null);
    setShowEditPhotoModal(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageMarkedForDeletion(false);

    if (!file.type.startsWith("image/")) {
      setPhotoError("Por favor selecciona un archivo de imagen válido");
      return;
    }

    const img = new window.Image();
    img.onload = () => {
      if (img.width !== img.height) {
        const size = Math.min(img.width, img.height);
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = size;
        canvas.height = size;

        const x = (img.width - size) / 2;
        const y = (img.height - size) / 2;

        ctx.drawImage(img, x, y, size, size, 0, 0, size, size);

        canvas.toBlob((blob) => {
          const croppedFile = new File([blob], file.name, { type: file.type });
          setSelectedFile(croppedFile);
          setPreviewUrl(URL.createObjectURL(croppedFile));
          setPhotoError(null);
          setCropMessage(
            "Esta imagen no es cuadrada, por lo que ha sido recortada"
          );
        }, file.type);
      } else {
        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
        setPhotoError(null);
        setCropMessage(null);
      }
    };
    img.src = URL.createObjectURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setPhotoError(null);
    const formDataUpload = new FormData();
    formDataUpload.append("profile_image", selectedFile);

    try {
      await apiClient.patch("users/me/", formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setShowEditPhotoModal(false);
      setSelectedFile(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      // Refresh user data in global context
      await refreshUser();
      setPopupMessage("Foto de perfil actualizada exitosamente");
      setPopupError(false);
      setPopupDuration(3000);
      setShowSuccessPopup(true);
    } catch (error) {
      console.error("Error subiendo imagen:", error);
      let errorMessage = "Error al subir la imagen";
      if (
        error.response?.data?.profile_image &&
        Array.isArray(error.response.data.profile_image)
      ) {
        errorMessage = error.response.data.profile_image[0];
      } else if (error.response?.data?.profile_image) {
        errorMessage = error.response.data.profile_image;
      }
      setPhotoError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async () => {
    try {
      await apiClient.patch("users/me/", { profile_image: null });
      setShowEditPhotoModal(false);
      // Refresh user data in global context
      await refreshUser();
      setPopupMessage("Imagen eliminada exitosamente");
      setPopupError(false);
      setPopupDuration(3000);
      setShowSuccessPopup(true);
    } catch (error) {
      console.error("Error eliminando imagen:", error);
      setPhotoError("Error al eliminar la imagen");
    }
  };

  // Clientify connection handlers
  const showConnectionError = (error, fallback) => {
    setPopupMessage(clientifyErrorMessage(error, fallback));
    setPopupError(true);
    setPopupDuration(5000);
    setShowSuccessPopup(true);
  };

  const refreshClientifyLists = async () => {
    const [connectableUsers, connections, users] = await Promise.all([
      listClientifyConnectableUsers(),
      listClientifyConnections(),
      listClientifyUsers(),
    ]);
    setUsersWithoutClientify(normalizeApiList(connectableUsers));
    setClientifyConnections(normalizeApiList(connections));
    setClientifyUsers(normalizeApiList(users));
  };

  const handleConnectClientifyUser = async () => {
    if (!selectedUserForClientify || !selectedClientifyUser) {
      setPopupMessage("Selecciona un usuario y un usuario de Clientify");
      setPopupError(true);
      setPopupDuration(3000);
      setShowSuccessPopup(true);
      return;
    }

    setConnectingClientifyUser(true);
    try {
      await connectClientifyUser({
        user: selectedUserForClientify,
        clientify_user_id: selectedClientifyUser,
      });
      setPopupMessage("Conexión creada exitosamente");
      setPopupError(false);
      setPopupDuration(3000);
      setShowSuccessPopup(true);
      await refreshClientifyLists();
      setSelectedUserForClientify(null);
      setSelectedClientifyUser(null);
    } catch (error) {
      console.error("Error connecting Clientify user:", error);
      showConnectionError(error, "Error al crear la conexión");
    } finally {
      setConnectingClientifyUser(false);
    }
  };

  const handleDeleteConnection = (connection) => {
    setConnectionToDelete(connection);
    setShowDeleteConnectionModal(true);
  };

  // SE Ranking connection handlers
  const refreshSerankingLists = async () => {
    // The sub-account list is refreshed along with the other two: it is what
    // carries `connected`, so without it a sub-account just taken would stay
    // selectable until the tab is reopened.
    const [connectableUsers, connections, subaccounts] = await Promise.all([
      listConnectableUsers(),
      listSeRankingAccounts(),
      listSeRankingSubaccounts(),
    ]);
    setUsersWithoutSeranking(normalizeApiList(connectableUsers));
    setSerankingConnections(normalizeApiList(connections));
    setSerankingSubaccounts(normalizeApiList(subaccounts));
  };

  const handleConnectSerankingUser = async () => {
    if (!selectedUserForSeranking || !selectedSerankingSubaccount) {
      setPopupMessage("Selecciona un usuario y una subcuenta de SE Ranking");
      setPopupError(true);
      setPopupDuration(3000);
      setShowSuccessPopup(true);
      return;
    }

    const subAccount = serankingSubaccounts.find(
      (account) =>
        String(account.seranking_user_id) ===
        String(selectedSerankingSubaccount)
    );

    setConnectingSerankingUser(true);
    try {
      await connectSeRankingAccount({
        user: selectedUserForSeranking,
        seranking_user_id: selectedSerankingSubaccount,
        seranking_email: subAccount?.seranking_email || "",
      });

      setPopupMessage("Conexión creada exitosamente");
      setPopupError(false);
      setPopupDuration(3000);
      setShowSuccessPopup(true);

      await refreshSerankingLists();

      setSelectedUserForSeranking(null);
      setSelectedSerankingSubaccount(null);
    } catch (error) {
      console.error("Error connecting user to SE Ranking:", error);

      let errorMessage = "Error al crear la conexión";
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setPopupMessage(errorMessage);
      setPopupError(true);
      setPopupDuration(5000);
      setShowSuccessPopup(true);
    } finally {
      setConnectingSerankingUser(false);
    }
  };

  const handleDeleteSerankingConnection = (connection) => {
    setSerankingConnectionToDelete(connection);
    setShowDeleteSerankingModal(true);
  };

  const handleConfirmDeleteSerankingConnection = async () => {
    // The connection row has its own id; the user's id addresses nothing here.
    const connectionId = serankingConnectionToDelete?.id;
    if (!connectionId) return;

    setDeletingSerankingConnection(true);
    try {
      await disconnectSeRankingAccount(connectionId);

      setPopupMessage("Conexión eliminada exitosamente");
      setPopupError(false);
      setPopupDuration(3000);
      setShowSuccessPopup(true);

      await refreshSerankingLists();

      setShowDeleteSerankingModal(false);
      setSerankingConnectionToDelete(null);
    } catch (error) {
      console.error("Error deleting SE Ranking connection:", error);

      let errorMessage = "Error al eliminar la conexión";
      // 409 means the sub-account still has clients hanging off it. Naming them
      // is the whole point of that answer: a generic "no se pudo" would leave
      // the superadmin with no idea of what to unlink first.
      if (error.response?.status === 409) {
        const clients = error.response?.data?.clients;
        errorMessage = Array.isArray(clients) && clients.length
          ? `No se puede eliminar: este usuario todavía tiene clientes vinculados a su subcuenta de SE Ranking (${clients.join(", ")}). Desvincula sus proyectos desde la pestaña Visibilidad de cada cliente.`
          : error.response?.data?.detail ||
            "No se puede eliminar: este usuario todavía tiene clientes vinculados a su subcuenta de SE Ranking.";
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setPopupMessage(errorMessage);
      setPopupError(true);
      setPopupDuration(5000);
      setShowSuccessPopup(true);
    } finally {
      setDeletingSerankingConnection(false);
    }
  };

  const handleConfirmDeleteConnection = async () => {
    if (!connectionToDelete) return;

    setDeletingConnection(true);
    try {
      await disconnectClientifyUser(connectionToDelete.user);
      setPopupMessage("Conexión eliminada exitosamente");
      setPopupError(false);
      setPopupDuration(3000);
      setShowSuccessPopup(true);
      await refreshClientifyLists();
      setShowDeleteConnectionModal(false);
      setConnectionToDelete(null);
    } catch (error) {
      console.error("Error deleting Clientify connection:", error);
      showConnectionError(error, "Error al eliminar la conexión");
    } finally {
      setDeletingConnection(false);
    }
  };

  const displayedImage =
    previewUrl ||
    (profile.profile_image && !imageMarkedForDeletion
      ? profile.profile_image
      : null);

  // Hook para manejar pegado de imágenes desde el portapapeles
  const handlePaste = usePasteSingleImage({
    onImageProcessed: (file, url) => {
      setImageMarkedForDeletion(false);
      setSelectedFile(file);
      setPreviewUrl(url);
      setPhotoError(null);
    },
    onCropMessage: setCropMessage,
    onError: setPhotoError,
  });

  // Escuchar eventos de paste a nivel de documento cuando el modal está abierto
  useEffect(() => {
    if (!showEditPhotoModal) return;

    document.addEventListener("paste", handlePaste);
    return () => {
      document.removeEventListener("paste", handlePaste);
    };
  }, [showEditPhotoModal, handlePaste]);

  const shouldLoadPodioData =
    interceptorReady && userRole === "superadmin" && activeTab === "podio";

  const shouldLoadClientifyData =
    interceptorReady && userRole === "superadmin" && activeTab === "clientify";

  const shouldLoadSerankingData =
    interceptorReady && userRole === "superadmin" && activeTab === "seranking";

  const {
    data: podioClientsData,
    error: podioClientsError,
    isLoading: podioClientsLoading,
  } = getData("clients/options/?ordering=name", shouldLoadPodioData, [
    podioRefreshKey,
  ]);

  // Fetch muted sources data using getData pattern
  const shouldFetchMutedSources =
    canViewMutedSources &&
    activeTab === "notifications" &&
    activeNotificationSubtab === "muted";

  // The SE Ranking tab needs the agencies too: it names the agency of every
  // agency_admin/consultant it lists, and the user payload only carries its id.
  const shouldFetchAgencies =
    userRole === "superadmin" &&
    (shouldFetchMutedSources || shouldLoadSerankingData);
  const shouldFetchClients =
    shouldFetchMutedSources && !!clientsEndpoint && userRole !== "superadmin";
  const shouldFetchMutedAgencies =
    shouldFetchMutedSources && userRole === "superadmin";
  const shouldFetchMutedClients =
    shouldFetchMutedSources && !!clientsEndpoint && userRole !== "superadmin";

  const { data: agenciesData, error: agenciesError } = getData(
    "agencies/",
    shouldFetchAgencies,
    [mutedRefreshKey]
  );

  const agencyNamesById = React.useMemo(() => {
    return normalizeApiList(agenciesData).reduce((names, agency) => {
      if (agency?.id !== undefined && agency?.id !== null) {
        names[String(agency.id)] = agency.name;
      }
      return names;
    }, {});
  }, [agenciesData]);

  const { data: clientsData, error: clientsError } = getData(
    clientsEndpoint || "clients/options/?ordering=name",
    shouldFetchClients,
    [mutedRefreshKey]
  );

  const { data: mutedAgenciesData, error: mutedAgenciesError } = getData(
    "dashboard/notifications/mutes/agencies/",
    shouldFetchMutedAgencies,
    [mutedRefreshKey]
  );

  const { data: mutedClientsData, error: mutedClientsError } = getData(
    "dashboard/notifications/mutes/clients/",
    shouldFetchMutedClients,
    [mutedRefreshKey]
  );

  const mutedSourcesLoading =
    shouldFetchMutedSources &&
    ((!agenciesData && !agenciesError && userRole === "superadmin") ||
      (!clientsData && !clientsError && clientsEndpoint) ||
      (!mutedAgenciesData &&
        !mutedAgenciesError &&
        userRole === "superadmin") ||
      (!mutedClientsData && !mutedClientsError && clientsEndpoint));

  // Process muted sources data
  React.useEffect(() => {
    if (!shouldFetchMutedSources) {
      return;
    }

    const normalizeList = (payload) => {
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.results)) return payload.results;
      return [];
    };

    // Check for errors
    const errors = [
      agenciesError,
      clientsError,
      mutedAgenciesError,
      mutedClientsError,
    ].filter(Boolean);
    if (errors.length > 0) {
      console.error("Error loading muted sources:", errors);
      // Only set error if it's not a 403 (forbidden)
      const nonForbiddenErrors = errors.filter(
        (err) => err.response?.status !== 403
      );
      if (nonForbiddenErrors.length > 0) {
        setMutedSourcesError(
          "No pudimos cargar tus fuentes silenciadas. Intenta nuevamente."
        );
      }
    } else {
      setMutedSourcesError(null);
    }

    // Update state with normalized data
    if (userRole === "superadmin" && agenciesData) {
      setAvailableAgencies(normalizeList(agenciesData));
    }

    if (clientsEndpoint && clientsData) {
      setAvailableClients(normalizeList(clientsData));
    }

    if (userRole === "superadmin" && mutedAgenciesData) {
      setMutedAgencies(normalizeList(mutedAgenciesData));
    }

    if (clientsEndpoint && mutedClientsData) {
      setMutedClients(normalizeList(mutedClientsData));
    }
  }, [
    shouldFetchMutedSources,
    agenciesData,
    clientsData,
    mutedAgenciesData,
    mutedClientsData,
    agenciesError,
    clientsError,
    mutedAgenciesError,
    mutedClientsError,
    userRole,
    clientsEndpoint,
  ]);

  React.useEffect(() => {
    if (!shouldLoadPodioData) {
      return;
    }

    let isMounted = true;

    const loadConnections = async () => {
      setPodioConnectionsLoading(true);
      setPodioConnectionsError(null);
      try {
        const response = await apiClient.get("podio/connections/");
        if (!isMounted) return;
        setPodioConnections(normalizeApiList(response.data));
      } catch (error) {
        console.error("Error loading Podio connections:", error);
        if (!isMounted) return;
        const detail =
          error.response?.data?.detail ||
          "No pudimos obtener las conexiones de Podio.";
        setPodioConnectionsError(detail);
      } finally {
        if (isMounted) {
          setPodioConnectionsLoading(false);
        }
      }
    };

    loadConnections();

    return () => {
      isMounted = false;
    };
  }, [shouldLoadPodioData, podioRefreshKey]);

  React.useEffect(() => {
    if (!shouldLoadPodioData || podioSpacesLoaded) {
      return;
    }

    let isMounted = true;

    const loadSpaces = async () => {
      setPodioSpacesLoading(true);
      setPodioSpacesError(null);
      try {
        const response = await apiClient.get("podio/spaces/");
        if (!isMounted) return;
        setPodioSpaces(normalizeApiList(response.data?.data || response.data));
        if (response.data?.rate_limit) {
          setPodioRateLimit(response.data.rate_limit);
        }
        setPodioSpacesLoaded(true);
      } catch (error) {
        console.error("Error loading Podio spaces:", error);
        if (!isMounted) return;
        let message =
          error.response?.data?.detail ||
          "No pudimos obtener los espacios de Podio.";
        if (error.response?.status === 503) {
          message =
            "No pudimos conectarnos con Podio. Intenta nuevamente en unos minutos.";
        }
        setPodioSpacesError(message);
      } finally {
        if (isMounted) {
          setPodioSpacesLoading(false);
        }
      }
    };

    loadSpaces();

    return () => {
      isMounted = false;
    };
  }, [shouldLoadPodioData]);

  // Fetch Clientify data when tab becomes active. Listing Clientify users is a
  // live call, so it only runs on the tab.
  React.useEffect(() => {
    if (!shouldLoadClientifyData) return;

    let isMounted = true;

    const fetchClientifyData = async () => {
      setClientifyUsersLoading(true);
      setClientifyUsersError(null);
      try {
        const users = await listClientifyUsers();
        if (!isMounted) return;
        setClientifyUsers(normalizeApiList(users));
      } catch (error) {
        console.error("Error fetching Clientify users:", error);
        if (!isMounted) return;
        setClientifyUsersError(
          clientifyErrorMessage(error, "Error al cargar usuarios de Clientify")
        );
      } finally {
        if (isMounted) setClientifyUsersLoading(false);
      }

      setUsersWithoutClientifyLoading(true);
      try {
        const users = await listClientifyConnectableUsers();
        if (!isMounted) return;
        setUsersWithoutClientify(normalizeApiList(users));
      } catch (error) {
        console.error("Error fetching users without Clientify:", error);
      } finally {
        if (isMounted) setUsersWithoutClientifyLoading(false);
      }

      setClientifyConnectionsLoading(true);
      try {
        const connections = await listClientifyConnections();
        if (!isMounted) return;
        setClientifyConnections(normalizeApiList(connections));
      } catch (error) {
        console.error("Error fetching Clientify connections:", error);
      } finally {
        if (isMounted) setClientifyConnectionsLoading(false);
      }
    };

    fetchClientifyData();

    return () => {
      isMounted = false;
    };
  }, [shouldLoadClientifyData]);

  // Fetch SE Ranking data when the tab becomes active. Listing sub-accounts is
  // a live call to SE Ranking's API, so it only runs on the tab, never on load.
  React.useEffect(() => {
    if (!shouldLoadSerankingData) return;

    let isMounted = true;

    const fetchSerankingData = async () => {
      setSerankingSubaccountsLoading(true);
      setSerankingSubaccountsError(null);
      try {
        const subaccounts = await listSeRankingSubaccounts();
        if (!isMounted) return;
        setSerankingSubaccounts(normalizeApiList(subaccounts));
      } catch (error) {
        console.error("Error fetching SE Ranking sub-accounts:", error);
        if (!isMounted) return;
        setSerankingSubaccountsError(
          error.response?.data?.detail ||
            "Error al cargar las subcuentas de SE Ranking"
        );
      } finally {
        if (isMounted) {
          setSerankingSubaccountsLoading(false);
        }
      }

      setUsersWithoutSerankingLoading(true);
      try {
        const users = await listConnectableUsers();
        if (!isMounted) return;
        setUsersWithoutSeranking(normalizeApiList(users));
      } catch (error) {
        console.error("Error fetching users without SE Ranking:", error);
      } finally {
        if (isMounted) {
          setUsersWithoutSerankingLoading(false);
        }
      }

      setSerankingConnectionsLoading(true);
      try {
        const connections = await listSeRankingAccounts();
        if (!isMounted) return;
        setSerankingConnections(normalizeApiList(connections));
      } catch (error) {
        console.error("Error fetching SE Ranking connections:", error);
      } finally {
        if (isMounted) {
          setSerankingConnectionsLoading(false);
        }
      }
    };

    fetchSerankingData();

    return () => {
      isMounted = false;
    };
  }, [shouldLoadSerankingData]);

  const allClientsForPodio = React.useMemo(() => {
    if (!shouldLoadPodioData || !podioClientsData) {
      return [];
    }
    return normalizeApiList(podioClientsData);
  }, [podioClientsData, shouldLoadPodioData]);

  const podioClientNameById = React.useMemo(() => {
    const map = new Map();
    allClientsForPodio.forEach((client) => {
      const label =
        client.name ||
        client.business_name ||
        client.fantasy_name ||
        `Cliente #${client.id}`;
      map.set(String(client.id), label);
    });
    return map;
  }, [allClientsForPodio]);

  const availableClientOptions = React.useMemo(() => {
    if (!allClientsForPodio.length) {
      return [];
    }
    const connectedClientIds = new Set(
      podioConnections
        .filter((connection) => connection.is_active)
        .map((connection) => String(connection.client))
    );
    return allClientsForPodio
      .filter((client) => !connectedClientIds.has(String(client.id)))
      .map((client) => ({
        value: client.id,
        label:
          client.name ||
          client.business_name ||
          client.fantasy_name ||
          `Cliente #${client.id}`,
      }));
  }, [allClientsForPodio, podioConnections]);

  const podioSpacesOptions = React.useMemo(() => {
    if (!podioSpaces.length) return [];
    return podioSpaces.map((space) => ({
      value: String(space.space_id),
      label: space.name || `Workspace ${space.space_id}`,
      disabled: space.status && space.status !== "active",
    }));
  }, [podioSpaces]);

  const podioConnectionsWithNames = React.useMemo(() => {
    if (!podioConnections.length) {
      return [];
    }
    return podioConnections.map((connection) => ({
      ...connection,
      clientName:
        podioClientNameById.get(String(connection.client)) ||
        `Cliente #${connection.client}`,
    }));
  }, [podioConnections, podioClientNameById]);

  const filteredAgencies = React.useMemo(() => {
    if (userRole !== "superadmin") {
      return [];
    }
    if (!availableAgencies?.length) {
      return [];
    }
    if (!selectedAgencyFilter) {
      return availableAgencies;
    }
    return availableAgencies.filter(
      (agency) => agency.id === selectedAgencyFilter
    );
  }, [availableAgencies, selectedAgencyFilter, userRole]);

  const filteredClients = React.useMemo(() => {
    if (!availableClients?.length) {
      return [];
    }
    if (!selectedClientFilter) {
      return availableClients;
    }
    return availableClients.filter(
      (client) => client.id === selectedClientFilter
    );
  }, [availableClients, selectedClientFilter]);

  const mutedSourcesData = React.useMemo(() => {
    if (!canViewMutedSources) {
      return { all: [], muted: [], unmuted: [] };
    }

    const grouped = {
      all: [],
      muted: [],
      unmuted: [],
    };

    const pushSource = (source) => {
      const targetArray = source.muted ? grouped.muted : grouped.unmuted;
      targetArray.push(source);
    };

    if (userRole === "superadmin") {
      filteredAgencies.forEach((agency) => {
        const muteRecord = mutedAgencies.find(
          (mute) => mute?.muted_agency?.id === agency.id
        );
        pushSource({
          key: `agency-${agency.id}`,
          type: "agency",
          id: agency.id,
          name: agency.name,
          subtitle: "Agencia",
          muted: Boolean(muteRecord),
          muteId: muteRecord?.id || null,
        });
      });
    }

    if (
      userRole === "superadmin" ||
      userRole === "agency_admin" ||
      userRole === "consultant"
    ) {
      filteredClients.forEach((client) => {
        const muteRecord = mutedClients.find(
          (mute) => mute?.muted_client?.id === client.id
        );
        pushSource({
          key: `client-${client.id}`,
          type: "client",
          id: client.id,
          name: client.name || client.business_name || "Cliente sin nombre",
          muted: Boolean(muteRecord),
          muteId: muteRecord?.id || null,
        });
      });
    }

    const sortByName = (list) =>
      [...list].sort((a, b) => {
        if (a.type === b.type) {
          return (a.name || "").localeCompare(b.name || "", "es", {
            sensitivity: "base",
          });
        }
        return a.type === "agency" ? -1 : 1;
      });

    const sortedMuted = sortByName(grouped.muted);
    const sortedUnmuted = sortByName(grouped.unmuted);

    return {
      all: sortByName([...grouped.unmuted, ...grouped.muted]),
      muted: sortedMuted,
      unmuted: sortedUnmuted,
    };
  }, [
    canViewMutedSources,
    filteredAgencies,
    filteredClients,
    mutedAgencies,
    mutedClients,
    userRole,
  ]);

  const handleMutedRefresh = () => {
    setMutedRefreshKey((prev) => prev + 1);
  };

  const handleClearMutedFilters = () => {
    setSelectedAgencyFilter(null);
    setSelectedClientFilter(null);
  };

  const handleMuteToggle = async (source, shouldMute) => {
    if (!interceptorReady) {
      return;
    }

    const sourceKey = `${source.type}-${source.id}`;
    setMuteProcessingMap((prev) => ({ ...prev, [sourceKey]: true }));

    const getMuteRecord = () =>
      source.type === "agency"
        ? mutedAgencies.find((mute) => mute?.muted_agency?.id === source.id)
        : mutedClients.find((mute) => mute?.muted_client?.id === source.id);

    // Guardar el estado anterior para poder revertir en caso de error
    const previousMuteRecord = getMuteRecord();
    const previousMutedAgencies = [...mutedAgencies];
    const previousMutedClients = [...mutedClients];

    // Actualización optimista: actualizar el estado inmediatamente antes de la llamada al servidor
    try {
      if (shouldMute) {
        // Crear un registro temporal para la actualización optimista
        const tempMuteRecord = {
          id: `temp-${source.id}`,
          [source.type === "agency" ? "muted_agency" : "muted_client"]: {
            id: source.id,
            name: source.name,
          },
        };

        if (source.type === "agency") {
          setMutedAgencies((prev) => {
            const filtered = prev.filter(
              (mute) => mute?.muted_agency?.id !== source.id
            );
            return [...filtered, tempMuteRecord];
          });
        } else {
          setMutedClients((prev) => {
            const filtered = prev.filter(
              (mute) => mute?.muted_client?.id !== source.id
            );
            return [...filtered, tempMuteRecord];
          });
        }

        // Realizar la llamada al servidor
        const endpoint =
          source.type === "agency"
            ? "dashboard/notifications/mutes/agencies/"
            : "dashboard/notifications/mutes/clients/";
        const payload =
          source.type === "agency"
            ? { agency_id: source.id }
            : { client_id: source.id };

        const response = await apiClient.post(endpoint, payload);

        // Reemplazar el registro temporal con el real
        if (source.type === "agency") {
          setMutedAgencies((prev) => {
            const filtered = prev.filter(
              (mute) => mute?.muted_agency?.id !== source.id
            );
            return [...filtered, response.data];
          });
        } else {
          setMutedClients((prev) => {
            const filtered = prev.filter(
              (mute) => mute?.muted_client?.id !== source.id
            );
            return [...filtered, response.data];
          });
        }

        setPopupMessage(
          `${source.type === "agency" ? "Agencia" : "Cliente"
          } silenciado correctamente`
        );
        setPopupError(false);
        setPopupDuration(1000);
        setShowSuccessPopup(true);
      } else {
        const muteRecord = previousMuteRecord;
        if (!muteRecord) {
          console.warn("No se encontró registro de silencio para desactivar");
          setMuteProcessingMap((prev) => {
            const next = { ...prev };
            delete next[sourceKey];
            return next;
          });
          return;
        }

        // Actualización optimista: eliminar inmediatamente del estado
        if (source.type === "agency") {
          setMutedAgencies((prev) =>
            prev.filter((mute) => mute.id !== muteRecord.id)
          );
        } else {
          setMutedClients((prev) =>
            prev.filter((mute) => mute.id !== muteRecord.id)
          );
        }

        // Realizar la llamada al servidor
        await apiClient.delete(
          `dashboard/notifications/mutes/${muteRecord.id}/`
        );

        setPopupMessage(
          `${source.type === "agency" ? "Agencia" : "Cliente"
          } reactivado correctamente`
        );
        setPopupError(false);
        setPopupDuration(1000);
        setShowSuccessPopup(true);
      }
    } catch (error) {
      console.error("Error updating mute:", error);

      // Revertir al estado anterior en caso de error
      setMutedAgencies(previousMutedAgencies);
      setMutedClients(previousMutedClients);

      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.non_field_errors?.[0] ||
        "Ocurrió un error al actualizar este silencio.";
      setPopupMessage(errorMessage);
      setPopupError(true);
      setPopupDuration(1000);
      setShowSuccessPopup(true);

      // Forzar refresh en caso de error para sincronizar el estado
      setMutedRefreshKey((prev) => prev + 1);
    } finally {
      setMuteProcessingMap((prev) => {
        const next = { ...prev };
        delete next[sourceKey];
        return next;
      });
    }
  };

  const getPromptErrorMessage = (error, fallbackMessage) =>
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    fallbackMessage;

  const handlePromptSaveSuccess = (message) => {
    setPopupMessage(message);
    setPopupError(false);
    setShowSuccessPopup(true);
  };

  const handlePromptSaveError = (error, fallbackMessage) => {
    const errorMessage = getPromptErrorMessage(error, fallbackMessage);
    setPopupMessage(errorMessage);
    setPopupError(true);
    setShowSuccessPopup(true);
  };

  // Prompt saves below update their cached query in place (no reload of the editor);
  // PromptField shows the saved/error feedback inline.
  const handleMarketingPromptSave = async (updatedPrompt) => {
    await apiClient.put("/leadmagnet/strategy/system-prompt/", {
      system_prompt: updatedPrompt,
    });
    queryClient.setQueryData(
      ["getData", "/leadmagnet/strategy/system-prompt/", promptsRefreshKey],
      (prev) => ({ ...prev, system_prompt: updatedPrompt })
    );
  };

  const handleContentPromptSave = async (promptKey, updatedPrompt) => {
    const data = await updateAiConfig(null, { [promptKey]: updatedPrompt });
    queryClient.setQueryData(aiConfigQueryKey(), data);
  };

  const handleReportPromptSave = async (promptKey, updatedPrompt) => {
    const data = await updateReportAiConfig(reportPromptsAgencyId, { [promptKey]: updatedPrompt });
    queryClient.setQueryData(reportAiConfigQueryKey(reportPromptsAgencyId), data);
  };

  const handleCopilotPromptSave = async (updatedPrompt) => {
    if (updatedPrompt.length > COPILOT_BEHAVIOR_PROMPT_MAX_LENGTH) {
      const message = `El prompt no puede superar los ${COPILOT_BEHAVIOR_PROMPT_MAX_LENGTH} caracteres (tiene ${updatedPrompt.length}).`;
      setPopupMessage(message);
      setPopupError(true);
      setShowSuccessPopup(true);
      throw new Error(message);
    }

    try {
      await apiClient.patch("copilot/agency-config/update/", {
        behavior_prompt: updatedPrompt,
      });
      setCopilotConfigRefreshKey((prev) => prev + 1);
      handlePromptSaveSuccess("Prompt del copiloto actualizado correctamente.");
    } catch (error) {
      handlePromptSaveError(
        error,
        error?.response?.data?.behavior_prompt?.[0] ||
          "No se pudo actualizar el prompt del copiloto."
      );
      throw error;
    }
  };

  const renderPromptConfigurationTab = () => {
    const configEntries = (isLoading, hasError, config, errorMessage, build) => {
      if (isLoading) return build(() => ({ status: "loading" }));
      if (hasError || !config) {
        return build(() => ({ status: "error", message: hasError ? errorMessage : "No hay configuración de prompts disponible." }));
      }
      return build((key, save) => ({ status: "ready", value: config[key] || "", onSave: (value) => save(key, value) }));
    };

    const marketing = marketingPromptError
      ? { status: "error", message: "No pudimos cargar el prompt de marketing." }
      : isLoadingMarketingPrompt
        ? { status: "loading" }
        : { status: "ready", value: marketingPromptData?.system_prompt || "", onSave: handleMarketingPromptSave };

    const content = (items) =>
      configEntries(
        isLoadingContentPrompts,
        contentPromptError,
        contentPromptConfig,
        "No pudimos cargar los prompts del generador de contenidos.",
        (state) => items.map((item) => ({ ...item, ...state(item.key, handleContentPromptSave) }))
      );

    const copilot = copilotConfigError
      ? { status: "error", message: "No pudimos cargar el prompt del copiloto." }
      : isLoadingCopilotConfig
        ? { status: "loading" }
        : { status: "ready", value: copilotConfigData?.behavior_prompt || "", onSave: handleCopilotPromptSave };

    const isAgencyAdmin = userRole === "agency_admin";
    const toReportEntries = (state) =>
      REPORT_PROMPTS.map((item) => ({
        ...item,
        id: item.key,
        editorKey: `${item.key}-${reportPromptsAgencyId}`,
        placeholder: "Sin contenido",
        ...state(item.key, handleReportPromptSave),
      }));
    const reports =
      isAgencyAdmin || reportPromptsAgencyId
        ? configEntries(
            isLoadingReportPrompts,
            reportPromptError,
            reportPromptData,
            "No pudimos cargar los prompts de informes.",
            toReportEntries
          )
        : toReportEntries(() => ({
            status: "error",
            message: "Elige una agencia arriba para ver y editar sus prompts de informes.",
          }));
    const agencyOptions = normalizeApiList(promptAgenciesData).map((agency) => ({
      value: agency.id,
      label: agency.name,
    }));

    const groups = [
      {
        title: "Copiloto",
        roles: ["agency_admin"],
        entries: [
          {
            id: "copilot",
            label: "Copiloto",
            description: `Qué debe priorizar el copiloto, qué vocabulario usar o qué evitar. Máximo ${COPILOT_BEHAVIOR_PROMPT_MAX_LENGTH} caracteres.`,
            Icon: Sparkles,
            placeholder: EMPTY_PROMPT_PLACEHOLDER,
            ...copilot,
          },
        ],
      },
      {
        title: "Marketing",
        roles: ["superadmin"],
        entries: [
          {
            id: "marketing_system",
            label: "Planes de marketing",
            description: "Prompt del sistema para generar estrategias de marketing.",
            Icon: Bot,
            placeholder: "Sin contenido",
            ...marketing,
          },
        ],
      },
      {
        title: "Adaptación por plataforma",
        roles: ["agency_admin"],
        entries: content(
          ADAPTATION_PROMPTS.map(({ key, label, color, PlatformIcon, description }) => ({
            key,
            label,
            Icon: ({ size }) => <PlatformIcon sx={{ fontSize: size + 2, color }} />,
            color,
            description,
            placeholder: EMPTY_PROMPT_PLACEHOLDER,
          }))
        ).map((entry) => ({ ...entry, id: entry.key })),
      },
      { title: "Informes", roles: ["superadmin", "agency_admin"], entries: reports },
    ].filter((group) => group.roles.includes(userRole));

    return (
      <Box sx={{ width: "100%", maxWidth: "1100px", minWidth: 0 }}>
        {!isAgencyAdmin && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2, maxWidth: 360 }}>
            <Label>Agencia de los prompts de informes</Label>
            <CustomSelector
              searchable
              placeholder="Selecciona una agencia"
              value={reportPromptsAgencyId}
              onChange={setReportPromptsAgencyId}
              options={agencyOptions}
            />
          </Box>
        )}
        <PromptsWorkspace
          groups={groups}
          theme={muiTheme}
          neutrals={neutrals}
          initialSelectedId={searchParams.get("prompt")}
        />
      </Box>
    );
  };

  const renderNotificationPreferences = () => {
    if (loadingPreferences) {
      return (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography sx={{ color: muiTheme.palette.text.secondary }}>
            Cargando tus preferencias...
          </Typography>
        </Box>
      );
    }

    if (preferencesError) {
      return (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
            gap: 2,
          }}
        >
          <Typography
            sx={{ color: muiTheme.palette.error.main, textAlign: "center" }}
          >
            {preferencesError}
          </Typography>
          <Button onClick={handleReloadPreferences}>Reintentar</Button>
        </Box>
      );
    }

    if (!availableNotificationTypes.length) {
      return (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
            gap: 1,
          }}
        >
          <Typography
            sx={{ color: muiTheme.palette.text.secondary, textAlign: "center" }}
          >
            No hay tipos de notificaciones configurables para tu rol actual.
          </Typography>
        </Box>
      );
    }

    return (
      <DataTable
        variant="plain"
        tableMinWidth={650}
        rows={availableNotificationTypes}
        columns={[
          {
            key: "type",
            header: "Tipo de notificación",
            width: "70%",
            cellSx: { whiteSpace: "normal" },
            render: (type) => (
              <>
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: "0.875rem" }}>
                  {type.title}
                </Typography>
                <Typography
                  sx={{ color: muiTheme.palette.text.secondary, fontSize: "0.875rem" }}
                >
                  {type.description}
                </Typography>
              </>
            ),
          },
          ...CHANNELS.map((channel) => ({
            key: channel.field,
            header: channel.label,
            align: "center",
            width: "15%",
            cellSx: { paddingLeft: "2px", paddingRight: "2px" },
            render: (type) => {
              // "central_announcement" tiene Dashboard siempre activo y no editable
              const isCentralAnnouncementDashboard =
                type.id === "central_announcement" &&
                channel.field === "show_in_dashboard";

              return (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <ToggleSwitch
                    checked={
                      isCentralAnnouncementDashboard
                        ? true
                        : (preferences[type.id]?.[channel.field] ?? false)
                    }
                    onChange={(e) =>
                      handlePreferenceChange(type.id, channel.field, e.target.checked)
                    }
                    disabled={isCentralAnnouncementDashboard}
                  />
                </Box>
              );
            },
          })),
        ]}
      />
    );
  };

  const renderMutedSources = () => {
    if (!canViewMutedSources) {
      return (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
            gap: 1,
          }}
        >
          <Typography
            sx={{ color: muiTheme.palette.text.secondary, textAlign: "center" }}
          >
            Tu rol no permite silenciar agencias o clientes.
          </Typography>
        </Box>
      );
    }

    const showInitialLoading =
      mutedSourcesLoading &&
      !availableAgencies.length &&
      !availableClients.length &&
      !mutedSourcesError;

    if (showInitialLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography sx={{ color: muiTheme.palette.text.secondary }}>
            Cargando tus fuentes silenciadas...
          </Typography>
        </Box>
      );
    }

    const clientOptions = [
      { value: "__all__", label: "Mostrar todos" },
      ...availableClients.map((client) => ({
        value: client.id,
        label: client.name || client.business_name || "Cliente sin nombre",
      })),
    ];

    const agencyOptions = [
      { value: "__all__", label: "Mostrar todas" },
      ...availableAgencies.map((agency) => ({
        value: agency.id,
        label: agency.name || "Agencia sin nombre",
      })),
    ];

    const { muted: mutedSources, all: allSources } = mutedSourcesData;

    // For superadmin, show agencies; for others, show clients
    const isSuperadmin = userRole === "superadmin";
    const filterLabel = isSuperadmin ? "Agencia" : "Cliente";
    const filterOptions = isSuperadmin ? agencyOptions : clientOptions;
    const filterValue = isSuperadmin
      ? selectedAgencyFilter
      : selectedClientFilter;
    const filterPlaceholder = isSuperadmin
      ? "Busca una agencia..."
      : "Busca un cliente...";
    const listTitle = isSuperadmin ? "Agencias" : "Clientes";
    const emptyMessage = isSuperadmin
      ? "Aún no tenemos agencias para mostrar."
      : "Aún no tenemos clientes para mostrar.";

    return (
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Filtro de Agencia/Cliente */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Label>{filterLabel}</Label>
          <CustomSelector
            searchable
            placeholder={
              filterValue === "__all__" || !filterValue
                ? isSuperadmin
                  ? "Mostrar todas"
                  : "Mostrar todos"
                : filterPlaceholder
            }
            value={filterValue}
            onChange={(value) => {
              if (isSuperadmin) {
                if (value !== selectedAgencyFilter) {
                  setMutedRefreshKey((prev) => prev + 1);
                }
                setSelectedAgencyFilter(value);
              } else {
                if (value !== selectedClientFilter) {
                  setMutedRefreshKey((prev) => prev + 1);
                }
                setSelectedClientFilter(value);
              }
            }}
            options={filterOptions}
            disabled={!filterOptions.length}
          />
        </Box>

        {mutedSourcesError && (
          <Box
            sx={{
              borderRadius: "12px",
              border: `1px solid ${muiTheme.palette.error.light}`,
              backgroundColor: `${muiTheme.palette.error.light}22`,
              padding: 2,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 1,
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
            }}
          >
            <Typography sx={{ color: muiTheme.palette.error.main }}>
              {mutedSourcesError}
            </Typography>
            <Button size="sm" variant="outline" onClick={handleMutedRefresh}>
              Reintentar
            </Button>
          </Box>
        )}

        {/* Lista de Agencias/Clientes con Toggle Switches */}
        <Box
          sx={{
            borderRadius: "12px",
            border: `1px solid ${muiTheme.palette.divider}`,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                padding: { xs: "10px 6px", md: "14px 16px" },
                borderBottom: `1px solid ${muiTheme.palette.divider}`,
                backgroundColor: neutrals.gray[50],
                boxShadow: `inset 0 -1px 0 0 ${muiTheme.palette.divider}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  color: muiTheme.palette.text.primary,
                  fontSize: { xs: "0.75rem", md: "0.875rem" },
                }}
              >
                {listTitle}
              </Typography>
              <Box
                sx={{
                  width: "44px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <BellOff size={16} color={muiTheme.palette.text.secondary} />
              </Box>
            </Box>
            <Box
              sx={{ position: "relative" }}
            >
              {mutedSourcesLoading && allSources.length > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: "rgba(255,255,255,0.6)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1,
                  }}
                >
                  <CircularProgress size={20} />
                </Box>
              )}
              {allSources.length ? (
                allSources.map((source, index) => {
                  const isProcessing = Boolean(muteProcessingMap[source.key]);
                  const toggleDisabled =
                    (source.type === "agency" && !canToggleAgencyMutes) ||
                    (source.type === "client" && !canToggleClientMutes);

                  return (
                    <Box
                      key={source.key}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        padding: 2,
                        backgroundColor:
                          index % 2 === 0
                            ? muiTheme.palette.background.paper
                            : muiTheme.palette.background.default,
                        borderBottom:
                          index === allSources.length - 1
                            ? "none"
                            : `1px solid ${muiTheme.palette.divider}`,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                      >
                        <Typography
                          sx={{ fontWeight: 500, fontSize: "0.875rem" }}
                        >
                          {source.name}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: "44px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <ToggleSwitch
                          checked={source.muted}
                          onChange={(e) =>
                            handleMuteToggle(source, e.target.checked)
                          }
                          disabled={toggleDisabled || isProcessing}
                        />
                      </Box>
                    </Box>
                  );
                })
              ) : (
                <Box
                  sx={{
                    padding: 4,
                    textAlign: "center",
                  }}
                >
                  <Typography sx={{ color: muiTheme.palette.text.secondary }}>
                    {emptyMessage}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    );
  };

  const formatPodioDate = (value) => {
    if (!value) {
      return "Nunca";
    }
    try {
      return new Date(value).toLocaleString("es-ES", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return value;
    }
  };

  const getSyncStatusDisplay = (status) => {
    switch (status) {
      case "idle":
        return { label: "Sincronizado", color: "green" };
      case "syncing":
        return { label: "Sincronizando", color: "yellow" };
      case "error":
        return { label: "Error", color: "red" };
      default:
        return { label: status || "Desconocido", color: "gray" };
    }
  };

  const handleCreatePodioConnection = async () => {
    if (!podioSelectedClient || !podioSelectedSpace) {
      return;
    }

    setCreatingPodioConnection(true);
    try {
      const matchingSpace = podioSpaces.find(
        (space) => String(space.space_id) === String(podioSelectedSpace)
      );
      const fallbackSpaceLabel =
        podioSpacesOptions.find(
          (option) => option.value === String(podioSelectedSpace)
        )?.label || `Workspace ${podioSelectedSpace}`;

      await apiClient.post("podio/connections/", {
        client: podioSelectedClient,
        space_id: String(
          matchingSpace ? matchingSpace.space_id : podioSelectedSpace
        ),
        space_name: matchingSpace?.name || fallbackSpaceLabel,
      });

      setPodioSelectedClient(null);
      setPodioSelectedSpace(null);
      setPodioRefreshKey((prev) => prev + 1);
      setPopupMessage(
        "Conexión creada. Ahora puedes iniciar la migración manual."
      );
      setPopupError(false);
      setPopupDuration(2000);
      setShowSuccessPopup(true);
    } catch (error) {
      console.error("Error creating Podio connection:", error);
      const detail =
        error.response?.data?.client?.[0] ||
        error.response?.data?.non_field_errors?.[0] ||
        error.response?.data?.detail ||
        "No pudimos crear la conexión con Podio.";
      setPopupMessage(detail);
      setPopupError(true);
      setPopupDuration(3000);
      setShowSuccessPopup(true);
    } finally {
      setCreatingPodioConnection(false);
    }
  };

  const handleTriggerPodioSync = async (connectionId) => {
    if (!connectionId) {
      return;
    }

    setSyncingConnectionId(connectionId);
    try {
      await apiClient.post(`podio/connections/${connectionId}/sync/`);
      setPodioRefreshKey((prev) => prev + 1);
      setPopupMessage("Migración iniciada. Revisa el estado en unos minutos.");
      setPopupError(false);
      setPopupDuration(2000);
      setShowSuccessPopup(true);
    } catch (error) {
      console.error("Error triggering Podio sync:", error);
      const detail =
        error.response?.data?.detail ||
        "No pudimos iniciar la migración. Intenta nuevamente.";
      setPopupMessage(detail);
      setPopupError(true);
      setPopupDuration(3000);
      setShowSuccessPopup(true);
    } finally {
      setSyncingConnectionId(null);
    }
  };

  const handleDeactivatePodioConnection = async (connectionId) => {
    if (!connectionId) {
      return;
    }

    try {
      await apiClient.delete(`podio/connections/${connectionId}/`);
      // Actualizar estado local directamente
      setPodioConnections((prev) =>
        prev.map((conn) =>
          conn.id === connectionId ? { ...conn, is_active: false } : conn
        )
      );
      setPopupMessage("Conexión desactivada correctamente.");
      setPopupError(false);
      setPopupDuration(1500);
      setShowSuccessPopup(true);
    } catch (error) {
      console.error("Error deactivating Podio connection:", error);
      const detail =
        error.response?.data?.detail ||
        "No pudimos desactivar esta conexión. Intenta nuevamente.";
      setPopupMessage(detail);
      setPopupError(true);
      setPopupDuration(3000);
      setShowSuccessPopup(true);
    }
  };

  const handleReactivatePodioConnection = async (connectionId) => {
    if (!connectionId) {
      return;
    }

    try {
      await apiClient.patch(`podio/connections/${connectionId}/`, {
        is_active: true,
      });
      // Actualizar estado local directamente
      setPodioConnections((prev) =>
        prev.map((conn) =>
          conn.id === connectionId ? { ...conn, is_active: true } : conn
        )
      );
      setPopupMessage("Conexión reactivada correctamente.");
      setPopupError(false);
      setPopupDuration(1500);
      setShowSuccessPopup(true);
    } catch (error) {
      console.error("Error reactivating Podio connection:", error);
      const detail =
        error.response?.data?.detail ||
        "No pudimos reactivar esta conexión. Intenta nuevamente.";
      setPopupMessage(detail);
      setPopupError(true);
      setPopupDuration(3000);
      setShowSuccessPopup(true);
    }
  };

  const handleGenerateTicketFreelanceInvite = async () => {
    setGeneratingTicketFreelanceInvite(true);
    try {
      const response = await apiClient.post("/users/generate_ticket_freelance_invite/");
      setGeneratedTicketFreelanceInvite(response.data);
      setTicketFreelancersRefreshKey((prev) => prev + 1);
      // Copy to clipboard directly after generating
      const url = response.data?.url;
      if (url) {
        try {
          if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(url);
          } else {
            const textArea = document.createElement("textarea");
            textArea.value = url;
            textArea.style.position = "fixed";
            textArea.style.left = "-999999px";
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            document.execCommand("copy");
            textArea.remove();
          }
          setFreelancerInviteCopied(true);
          setTimeout(() => setFreelancerInviteCopied(false), 2000);
        } catch (_) {
          // clipboard failed silently
        }
      }
      setPopupMessage("Enlace generado y copiado al portapapeles.");
      setPopupError(false);
      setPopupDuration(2000);
      setShowSuccessPopup(true);
    } catch (error) {
      const detail =
        error.response?.data?.detail ||
        "No pudimos generar el enlace de freelance tickets.";
      setPopupMessage(detail);
      setPopupError(true);
      setPopupDuration(3000);
      setShowSuccessPopup(true);
    } finally {
      setGeneratingTicketFreelanceInvite(false);
    }
  };

  const handleCopyTicketFreelanceInvite = async () => {
    const url = generatedTicketFreelanceInvite?.url;
    if (!url) {
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      setPopupMessage("Enlace copiado al portapapeles.");
      setPopupError(false);
      setPopupDuration(1600);
      setShowSuccessPopup(true);
    } catch (error) {
      setPopupMessage("No se pudo copiar el enlace.");
      setPopupError(true);
      setPopupDuration(2500);
      setShowSuccessPopup(true);
    }
  };

  const renderTicketFreelancersTab = () => {
    if (userRole !== "superadmin") {
      return null;
    }

    const ticketFreelancers = normalizeApiList(ticketFreelancersData);

    return (
      <Card sx={{ width: "100%", maxWidth: "960px" }}>
        <CardContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", md: "center" },
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600, mb: 0.5 }}>
                  Freelance tickets
                </Typography>
                <Typography variant="body2" sx={{ color: muiTheme.palette.text.secondary }}>
                  Genera enlaces de alta y consulta los usuarios que solo pueden trabajar tickets asignados.
                </Typography>
              </Box>

              <Button
                variant="outline"
                onClick={handleGenerateTicketFreelanceInvite}
                disabled={generatingTicketFreelanceInvite}
                sx={{ gap: 1 }}
              >
                {freelancerInviteCopied ? <Check size={16} /> : <Link2 size={16} />}
                {generatingTicketFreelanceInvite
                  ? "Generando..."
                  : freelancerInviteCopied
                    ? "¡Copiado!"
                    : "Invitar Freelancer"}
              </Button>
            </Box>




            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Usuarios dados de alta
              </Typography>

              {ticketFreelancersLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : ticketFreelancers.length === 0 ? (
                <Typography variant="body2" sx={{ color: muiTheme.palette.text.secondary }}>
                  Todavía no hay freelance tickets creados.
                </Typography>
              ) : (
                <DataTable
                  variant="plain"
                  size="sm"
                  tableMinWidth={500}
                  rows={ticketFreelancers}
                  columns={[
                    {
                      key: "name",
                      header: "Nombre",
                      minWidth: { xs: "130px", md: "170px" },
                      render: (ticketFreelancer) => (
                        <Typography sx={{ fontWeight: 600 }}>
                          {`${ticketFreelancer.first_name || ""} ${ticketFreelancer.last_name || ""}`.trim() || "Sin nombre"}
                        </Typography>
                      ),
                    },
                    {
                      key: "email",
                      header: "Email",
                      minWidth: { xs: "160px", md: "210px" },
                      render: (ticketFreelancer) => (
                        <Typography sx={{ fontWeight: 500 }}>{ticketFreelancer.email}</Typography>
                      ),
                    },
                    {
                      key: "created_at",
                      header: "Alta",
                      minWidth: { xs: "100px", md: "130px" },
                      render: (ticketFreelancer) => (
                        <Typography sx={{ color: muiTheme.palette.text.secondary, fontSize: "0.85rem" }}>
                          {ticketFreelancer.created_at
                            ? new Date(ticketFreelancer.created_at).toLocaleDateString("es-ES")
                            : "-"}
                        </Typography>
                      ),
                    },
                  ]}
                />
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderClientifyConnectionTab = () => {
    if (userRole !== "superadmin") {
      return null;
    }

    if (!interceptorReady) {
      return (
        <Card
          sx={{
            width: "fit-content",
            maxWidth: "1200px",
            borderRadius: "12px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
          }}
        >
          <CardContent>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <CircularProgress />
              <Typography sx={{ color: muiTheme.palette.text.secondary }}>
                Preparando autenticación...
              </Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    const userOptions = usersWithoutClientify
      .map((user) => ({
        value: user.id,
        label: serankingUserLabel(user, agencyNamesById),
      }))
      .sort((left, right) => left.label.localeCompare(right.label, "es"));

    // Taken Clientify users stay visible but disabled, saying who has them.
    const clientifyUserOptions = clientifyUsers.map((clientifyUser) => ({
      value: clientifyUser.clientify_user_id,
      disabled: Boolean(clientifyUser.connected),
      label: `${
        [clientifyUser.full_name, clientifyUser.email].filter(Boolean).join(" - ") ||
        `Usuario ${clientifyUser.clientify_user_id}`
      }${
        clientifyUser.connected
          ? ` · ya conectado${
              clientifyUser.connected_user_email
                ? ` a ${clientifyUser.connected_user_email}`
                : ""
            }`
          : ""
      }`,
    }));

    const disableConnectButton =
      !selectedUserForClientify || !selectedClientifyUser || connectingClientifyUser;

    return (
      <Card
        sx={{
          width: "100%",
          maxWidth: "1400px",
          minWidth: 0,
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography sx={{ fontWeight: 600 }}>Conexión con Clientify</Typography>
            <Typography sx={{ color: muiTheme.palette.text.secondary, fontSize: "0.875rem" }}>
              Conecta cada administrador de agencia o consultor con su usuario de
              Clientify. Cada uno verá y gestionará solo sus propios contactos y
              oportunidades desde Leads.
            </Typography>
          </Box>

          {/* Nueva conexión */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                flex: "1 1 260px",
                minWidth: { xs: "100%", md: "260px" },
              }}
            >
              <Label>Usuario</Label>
              <CustomSelector
                searchable
                placeholder="Selecciona un usuario"
                value={selectedUserForClientify}
                onChange={setSelectedUserForClientify}
                options={userOptions}
                disabled={usersWithoutClientifyLoading || connectingClientifyUser}
              />
              {usersWithoutClientifyLoading && (
                <Typography variant="caption" sx={{ color: muiTheme.palette.text.secondary, mt: 0.5 }}>
                  Cargando usuarios...
                </Typography>
              )}
              {!userOptions.length && !usersWithoutClientifyLoading && (
                <Typography variant="caption" sx={{ color: muiTheme.palette.text.secondary, mt: 0.5 }}>
                  Todos los administradores de agencia y consultores ya tienen una
                  conexión activa.
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                flex: "2 1 420px",
                minWidth: { xs: "100%", md: "420px" },
              }}
            >
              <Label>Usuario de Clientify</Label>
              <CustomSelector
                searchable
                placeholder="Selecciona un usuario de Clientify"
                value={selectedClientifyUser}
                onChange={setSelectedClientifyUser}
                options={clientifyUserOptions}
                disabled={clientifyUsersLoading || connectingClientifyUser}
              />
              {clientifyUsersLoading && (
                <Typography variant="caption" sx={{ color: muiTheme.palette.text.secondary, mt: 0.5 }}>
                  Cargando usuarios de Clientify...
                </Typography>
              )}
              {clientifyUsersError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {clientifyUsersError}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                flex: "0 0 140px",
                minWidth: { xs: "100%", md: "140px" },
              }}
            >
              <Label sx={{ visibility: "hidden" }}>Acción</Label>
              <Button
                onClick={handleConnectClientifyUser}
                disabled={disableConnectButton}
                sx={{ width: "100%" }}
              >
                {connectingClientifyUser ? "Conectando..." : "Conectar"}
              </Button>
            </Box>
          </Box>

          <Divider />

          {/* Conexiones actuales */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {clientifyConnectionsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : clientifyConnections.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4, color: muiTheme.palette.text.secondary }}>
                <Typography>No hay conexiones activas</Typography>
              </Box>
            ) : (
              <DataTable
                variant="plain"
                size="sm"
                tableMinWidth={650}
                rows={clientifyConnections}
                getRowId={(connection) => connection.user}
                columns={[
                  {
                    key: "user",
                    header: "Usuario",
                    render: (connection) => {
                      const connectedUser = {
                        id: connection.user,
                        full_name: connection.user_name,
                        email: connection.user_email,
                      };
                      return (
                        <>
                          <Typography sx={{ fontWeight: 600 }}>
                            {userDisplayName(connectedUser)}
                          </Typography>
                          {connectedUser.email &&
                            connectedUser.email !== userDisplayName(connectedUser) && (
                              <Typography sx={{ color: muiTheme.palette.text.secondary, fontSize: "0.8rem" }}>
                                {connectedUser.email}
                              </Typography>
                            )}
                        </>
                      );
                    },
                  },
                  {
                    key: "role",
                    header: "Rol",
                    render: (connection) => (
                      <Typography sx={{ color: muiTheme.palette.text.secondary }}>
                        {ROLE_LABELS[connection.user_role] || connection.user_role || "-"}
                      </Typography>
                    ),
                  },
                  {
                    key: "agency",
                    header: "Agencia",
                    render: (connection) => (
                      <Typography sx={{ color: muiTheme.palette.text.secondary }}>
                        {userAgencyName(
                          { agency: connection.agency, agency_name: connection.agency_name },
                          agencyNamesById
                        ) || "-"}
                      </Typography>
                    ),
                  },
                  {
                    key: "clientify_user",
                    header: "Usuario de Clientify",
                    render: (connection) => (
                      <Typography sx={{ fontWeight: 500 }}>
                        {clientifyUserLabelById.get(String(connection.clientify_user_id)) ||
                          `Usuario #${connection.clientify_user_id}`}
                      </Typography>
                    ),
                  },
                  {
                    key: "actions",
                    header: "Acciones",
                    align: "center",
                    render: (connection) => (
                      <Tooltip title="Eliminar conexión">
                        <IconButton
                          onClick={() => handleDeleteConnection(connection)}
                          sx={{
                            color: muiTheme.palette.error.main,
                            "&:hover": { backgroundColor: muiTheme.palette.error.light + "20" },
                          }}
                        >
                          <Trash2 size={18} />
                        </IconButton>
                      </Tooltip>
                    ),
                  },
                ]}
              />
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderSeRankingConnectionTab = () => {
    if (userRole !== "superadmin") {
      return null;
    }

    if (!interceptorReady) {
      return (
        <Card
          sx={{
            width: "fit-content",
            maxWidth: "1200px",
            borderRadius: "12px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
          }}
        >
          <CardContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CircularProgress />
              <Typography sx={{ color: muiTheme.palette.text.secondary }}>
                Preparando autenticación...
              </Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    const userOptions = usersWithoutSeranking
      .map((user) => ({
        value: user.id,
        label: serankingUserLabel(user, agencyNamesById),
      }))
      .sort((left, right) => left.label.localeCompare(right.label, "es"));

    // A sub-account already taken is shown but not selectable: the backend
    // would reject it anyway, and saying who has it is more useful than
    // hiding it.
    const serankingSubaccountOptions = serankingSubaccounts.map((account) => ({
      value: account.seranking_user_id,
      disabled: Boolean(account.connected),
      label: `${
        [account.name, account.seranking_email].filter(Boolean).join(" - ") ||
        `Subcuenta ${account.seranking_user_id}`
      }${
        account.connected
          ? ` · ya conectada${
              account.connected_user_email
                ? ` a ${account.connected_user_email}`
                : ""
            }`
          : ""
      }`,
    }));

    const disableConnectButton =
      !selectedUserForSeranking ||
      !selectedSerankingSubaccount ||
      connectingSerankingUser;

    return (
      <Card
        sx={{
          width: "100%",
          maxWidth: "1400px",
          minWidth: 0,
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography sx={{ fontWeight: 600 }}>
              Conexión con SE Ranking
            </Typography>
            <Typography
              sx={{
                color: muiTheme.palette.text.secondary,
                fontSize: "0.875rem",
              }}
            >
              Conecta cada administrador de agencia o consultor con su subcuenta
              de SE Ranking. Después, cada uno podrá vincular sus clientes con
              uno de los proyectos de su propia subcuenta desde la pestaña
              Visibilidad.
            </Typography>
          </Box>

          {/* Nueva conexión */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "flex-start",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  flex: "1 1 260px",
                  minWidth: { xs: "100%", md: "260px" },
                }}
              >
                <Label>Usuario</Label>
                <CustomSelector
                  searchable
                  placeholder="Selecciona un usuario"
                  value={selectedUserForSeranking}
                  onChange={setSelectedUserForSeranking}
                  options={userOptions}
                  disabled={
                    usersWithoutSerankingLoading || connectingSerankingUser
                  }
                />
                {usersWithoutSerankingLoading && (
                  <Typography
                    variant="caption"
                    sx={{ color: muiTheme.palette.text.secondary, mt: 0.5 }}
                  >
                    Cargando usuarios...
                  </Typography>
                )}
                {!userOptions.length && !usersWithoutSerankingLoading && (
                  <Typography
                    variant="caption"
                    sx={{ color: muiTheme.palette.text.secondary, mt: 0.5 }}
                  >
                    Todos los administradores de agencia y consultores ya
                    tienen una conexión activa.
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  flex: "2 1 420px",
                  minWidth: { xs: "100%", md: "420px" },
                }}
              >
                <Label>Subcuenta de SE Ranking</Label>
                <CustomSelector
                  searchable
                  placeholder="Selecciona una subcuenta"
                  value={selectedSerankingSubaccount}
                  onChange={setSelectedSerankingSubaccount}
                  options={serankingSubaccountOptions}
                  disabled={
                    serankingSubaccountsLoading || connectingSerankingUser
                  }
                />
                {serankingSubaccountsLoading && (
                  <Typography
                    variant="caption"
                    sx={{ color: muiTheme.palette.text.secondary, mt: 0.5 }}
                  >
                    Cargando subcuentas de SE Ranking...
                  </Typography>
                )}
                {serankingSubaccountsError && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {serankingSubaccountsError}
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  flex: "0 0 140px",
                  minWidth: { xs: "100%", md: "140px" },
                }}
              >
                <Label sx={{ visibility: "hidden" }}>Acción</Label>
                <Button
                  onClick={handleConnectSerankingUser}
                  disabled={disableConnectButton}
                  sx={{ width: "100%" }}
                >
                  {connectingSerankingUser ? "Conectando..." : "Conectar"}
                </Button>
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Conexiones actuales */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {serankingConnectionsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : serankingConnections.length === 0 ? (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  color: muiTheme.palette.text.secondary,
                }}
              >
                <Typography>No hay conexiones activas</Typography>
              </Box>
            ) : (
              <DataTable
                variant="plain"
                size="sm"
                tableMinWidth={650}
                rows={serankingConnections}
                getRowId={(connection, index) => connection.id ?? index}
                columns={[
                  {
                    key: "user",
                    header: "Usuario",
                    minWidth: { xs: "130px", md: "170px" },
                    render: (connection) => {
                      // The connection serializer is flat: the user and the
                      // sub-account are spread across `user_*` / `seranking_*`
                      // fields on the row itself, not nested objects.
                      const connectedUser = {
                        id: connection.user,
                        full_name: connection.user_name,
                        email: connection.user_email,
                      };
                      return (
                        <>
                          <Typography sx={{ fontWeight: 600 }}>
                            {userDisplayName(connectedUser)}
                          </Typography>
                          {connectedUser.email &&
                            connectedUser.email !== userDisplayName(connectedUser) && (
                              <Typography
                                sx={{ color: muiTheme.palette.text.secondary, fontSize: "0.8rem" }}
                              >
                                {connectedUser.email}
                              </Typography>
                            )}
                        </>
                      );
                    },
                  },
                  {
                    key: "role",
                    header: "Rol",
                    minWidth: { xs: "110px", md: "150px" },
                    render: (connection) => (
                      <Typography sx={{ color: muiTheme.palette.text.secondary }}>
                        {ROLE_LABELS[connection.user_role] || connection.user_role || "-"}
                      </Typography>
                    ),
                  },
                  {
                    key: "agency",
                    header: "Agencia",
                    minWidth: { xs: "110px", md: "150px" },
                    render: (connection) => (
                      <Typography sx={{ color: muiTheme.palette.text.secondary }}>
                        {userAgencyName(
                          { agency: connection.agency, agency_name: connection.agency_name },
                          agencyNamesById
                        ) || "-"}
                      </Typography>
                    ),
                  },
                  {
                    key: "seranking_account",
                    header: "Subcuenta de SE Ranking",
                    minWidth: { xs: "140px", md: "190px" },
                    render: (connection) => (
                      <Typography sx={{ fontWeight: 500 }}>
                        {connection.seranking_email ||
                          `Subcuenta #${connection.seranking_user_id}`}
                      </Typography>
                    ),
                  },
                  // A connection with clients on it cannot be deleted, so the
                  // count is shown next to the delete button rather than only
                  // surfacing as a 409.
                  {
                    key: "linked_clients",
                    header: "Clientes",
                    align: "center",
                    minWidth: { xs: "90px", md: "120px" },
                    render: (connection) => (
                      <Typography sx={{ color: muiTheme.palette.text.secondary }}>
                        {connection.linked_clients_count ?? 0}
                      </Typography>
                    ),
                  },
                  {
                    key: "actions",
                    header: "Acciones",
                    align: "center",
                    minWidth: { xs: "100px", md: "130px" },
                    render: (connection) => (
                      <Tooltip title="Eliminar conexión">
                        <IconButton
                          onClick={() => handleDeleteSerankingConnection(connection)}
                          sx={{
                            color: muiTheme.palette.error.main,
                            "&:hover": { backgroundColor: muiTheme.palette.error.light + "20" },
                          }}
                        >
                          <Trash2 size={18} />
                        </IconButton>
                      </Tooltip>
                    ),
                  },
                ]}
              />
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderPodioMigrationTab = () => {
    if (userRole !== "superadmin") {
      return null;
    }

    if (!interceptorReady) {
      return (
        <Card
          sx={{
            width: "fit-content",
            maxWidth: "1500px",
            borderRadius: "12px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
          }}
        >
          <CardContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              <CircularProgress />
              <Typography sx={{ color: muiTheme.palette.text.secondary }}>
                Preparando autenticación...
              </Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    const disableCreateButton =
      !podioSelectedClient ||
      !podioSelectedSpace ||
      creatingPodioConnection ||
      podioSpacesLoading;

    const showConnectionsSkeleton =
      podioConnectionsLoading && !podioConnectionsWithNames.length;
    const showConnectionsEmpty =
      !podioConnectionsLoading &&
      !podioConnectionsError &&
      podioConnectionsWithNames.length === 0;

    const clientsErrorMessage =
      podioClientsError?.response?.data?.detail || podioClientsError?.message;

    return (
      <Card
        sx={{
          width: "fit-content",
          maxWidth: "1500px",
          minWidth: 0,
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography sx={{ fontWeight: 600 }}>
              Migración desde Podio
            </Typography>
            <Typography
              sx={{
                color: muiTheme.palette.text.secondary,
                fontSize: "0.875rem",
              }}
            >
              Conecta el workspace de Podio correcto y lanza una importación
              manual. También ejecutaremos un auto-sync cada día a las 02:00.
            </Typography>
            <Typography
              sx={{
                color: muiTheme.palette.text.secondary,
                fontSize: "0.875rem",
              }}
            >
              Consulta los estados de las sincronizaciones en la tabla inferior.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={4}>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Label>Cliente</Label>
                  <CustomSelector
                    searchable
                    placeholder="Selecciona un cliente"
                    value={podioSelectedClient}
                    onChange={setPodioSelectedClient}
                    options={availableClientOptions}
                    disabled={
                      !availableClientOptions.length || creatingPodioConnection
                    }
                  />
                  {podioClientsLoading && (
                    <Typography
                      variant="caption"
                      sx={{ color: muiTheme.palette.text.secondary, mt: 0.5 }}
                    >
                      Cargando clientes disponibles...
                    </Typography>
                  )}
                  {!availableClientOptions.length &&
                    !podioClientsLoading &&
                    !clientsErrorMessage && (
                      <Typography
                        variant="caption"
                        sx={{ color: muiTheme.palette.text.secondary, mt: 0.5 }}
                      >
                        Todos los clientes disponibles ya cuentan con una
                        migración activa.
                      </Typography>
                    )}
                  {clientsErrorMessage && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      {clientsErrorMessage}
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Label>Workspace de Podio</Label>
                  <CustomSelector
                    searchable
                    placeholder="Busca un workspace"
                    value={podioSelectedSpace}
                    onChange={setPodioSelectedSpace}
                    options={podioSpacesOptions}
                    disabled={
                      !podioSpacesOptions.length ||
                      podioSpacesLoading ||
                      creatingPodioConnection
                    }
                  />
                  {podioSpacesLoading && (
                    <Typography
                      variant="caption"
                      sx={{ color: muiTheme.palette.text.secondary, mt: 0.5 }}
                    >
                      Consultando workspaces disponibles...
                    </Typography>
                  )}
                  {podioSpacesError && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      {podioSpacesError}
                    </Typography>
                  )}
                  {podioSpacesLoading && !podioRateLimit ? (
                    <Box
                      sx={{
                        mt: 0.5,
                        height: 16,
                        width: 180,
                        backgroundColor: neutrals.gray[200],
                        borderRadius: 1,
                        animation: "pulse 1.5s ease-in-out infinite",
                        "@keyframes pulse": {
                          "0%, 100%": { opacity: 1 },
                          "50%": { opacity: 0.5 },
                        },
                      }}
                    />
                  ) : podioRateLimit ? (
                    <Typography
                      variant="caption"
                      sx={{ color: muiTheme.palette.text.secondary, mt: 0.5 }}
                    >
                      Peticiones restantes: {podioRateLimit.remaining}/{podioRateLimit.total} ({podioRateLimit.percentage}%)
                    </Typography>
                  ) : null}
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Label sx={{ visibility: "hidden" }}>Acción</Label>
                  <Button
                    onClick={handleCreatePodioConnection}
                    disabled={disableCreateButton}
                    sx={{ width: "100%" }}
                  >
                    {creatingPodioConnection
                      ? "Creando conexión..."
                      : "Conectar workspace"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {showConnectionsSkeleton ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                py: 4,
              }}
            >
              <CircularProgress />
              <Typography sx={{ color: muiTheme.palette.text.secondary }}>
                Consultando conexiones...
              </Typography>
            </Box>
          ) : podioConnectionsError ? (
            <Box
              sx={{
                borderRadius: "12px",
                border: `1px solid ${muiTheme.palette.error.light}`,
                padding: 2,
              }}
            >
              <Typography color="error">{podioConnectionsError}</Typography>
            </Box>
          ) : showConnectionsEmpty ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                py: 4,
              }}
            >
              <Typography sx={{ color: muiTheme.palette.text.secondary }}>
                Aún no hay migraciones registradas.
              </Typography>
              <Typography
                sx={{
                  color: muiTheme.palette.text.secondary,
                  fontSize: "0.85rem",
                }}
              >
                Crea una conexión para importar el histórico desde Podio.
              </Typography>
            </Box>
          ) : (
            <DataTable
              variant="plain"
              size="sm"
              tableMinWidth={650}
              rows={podioConnectionsWithNames}
              columns={[
                {
                  key: "client",
                  header: "Cliente/Conexión",
                  minWidth: { xs: "110px", md: "150px" },
                  render: (connection) => (
                    <>
                      <Typography sx={{ fontWeight: 600 }}>{connection.clientName}</Typography>
                      <Typography variant="caption" sx={{ color: muiTheme.palette.text.secondary }}>
                        {connection.is_active ? "Conexión activa" : "Conexión desactivada"}
                      </Typography>
                    </>
                  ),
                },
                {
                  key: "workspace",
                  header: "Workspace",
                  minWidth: { xs: "120px", md: "170px" },
                  render: (connection) => (
                    <>
                      <Typography sx={{ fontWeight: 500 }}>{connection.space_name}</Typography>
                      {connection.url && (
                        <Typography
                          component="a"
                          href={connection.url}
                          target="_blank"
                          rel="noreferrer"
                          sx={{
                            color: muiTheme.palette.primary.main,
                            fontSize: "0.8rem",
                            textDecoration: "underline",
                            textUnderlineOffset: "3px",
                            display: "inline-flex",
                            mt: 0.5,
                          }}
                        >
                          Abrir en Podio
                        </Typography>
                      )}
                    </>
                  ),
                },
                {
                  key: "status",
                  header: "Estado",
                  minWidth: { xs: "110px", md: "130px" },
                  render: (connection) => {
                    const syncDisplay = getSyncStatusDisplay(connection.sync_status);
                    return (
                      <>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          <Badge variant="outline" color={syncDisplay.color}>
                            {syncDisplay.label}
                          </Badge>
                        </Box>
                        {connection.last_sync_error && (() => {
                          const MAX_ERROR_LENGTH = 100;
                          const errorText = connection.last_sync_error;
                          const shouldTruncate = errorText.length > MAX_ERROR_LENGTH;
                          const isExpanded = expandedSyncErrors[connection.id];
                          const displayText = shouldTruncate && !isExpanded
                            ? errorText.substring(0, MAX_ERROR_LENGTH) + "..."
                            : errorText;

                          return (
                            <Box translate="no" sx={{ mt: 0.5 }}>
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{
                                  display: "block",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                }}
                              >
                                {displayText}
                              </Typography>
                              {shouldTruncate && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedSyncErrors((prev) => ({
                                      ...prev,
                                      [connection.id]: !prev[connection.id],
                                    }));
                                  }}
                                  sx={{
                                    padding: "2px 6px",
                                    fontSize: "0.7rem",
                                    color: muiTheme.palette.primary.main,
                                    minWidth: "auto",
                                    mt: 0.5,
                                    "&:hover": {
                                      backgroundColor: "transparent",
                                      textDecoration: "underline",
                                    },
                                  }}
                                >
                                  {isExpanded ? "Mostrar menos" : "Mostrar completo"}
                                </Button>
                              )}
                            </Box>
                          );
                        })()}
                      </>
                    );
                  },
                },
                {
                  key: "last_synced_at",
                  header: "Última sincronización",
                  minWidth: { xs: "120px", md: "170px" },
                  render: (connection) => (
                    <Typography>{formatPodioDate(connection.last_synced_at)}</Typography>
                  ),
                },
                {
                  key: "actions",
                  header: "Acciones",
                  align: "center",
                  minWidth: { xs: "120px", md: "150px" },
                  render: (connection) => (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        flexWrap: "nowrap",
                        gap: 1,
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {connection.is_active && (
                        <Button
                          size="sm"
                          onClick={() => handleTriggerPodioSync(connection.id)}
                          disabled={
                            syncingConnectionId === connection.id ||
                            connection.sync_status === "syncing"
                          }
                        >
                          {syncingConnectionId === connection.id ? "Iniciando..." : "Migrar ahora"}
                        </Button>
                      )}
                      {connection.is_active ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeactivatePodioConnection(connection.id)}
                        >
                          Desactivar
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReactivatePodioConnection(connection.id)}
                        >
                          Reactivar
                        </Button>
                      )}
                    </Box>
                  ),
                },
              ]}
            />
          )}
        </CardContent>
      </Card>
    );
  };

  if (loadingProfile) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Cabecera del perfil (foto, nombre y email) en la sección Perfil
  const profileHeader = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: 2,
        pb: 2,
        borderBottom: `1px solid ${muiTheme.palette.divider}`,
        flexWrap: "wrap",
      }}
    >
      {/* Profile Image - Smaller */}
      <Box
        onClick={handleEditPhoto}
        sx={{
          cursor: "pointer",
          position: "relative",
          transition: "all 0.2s",
          "&:hover": {
            "& .avatar-overlay": {
              opacity: 1,
            },
          },
        }}
      >
        <Avatar
          sx={{
            width: 60,
            height: 60,
            border: `2px solid ${colors.primary.main}26`,
          }}
          src={profile.profile_image}
        >
          <AvatarFallback
            sx={{
              backgroundColor: brand.avatar,
              color: "#fff",
              fontSize: "1.5rem",
              fontWeight: 600,
            }}
          >
            {initials}
          </AvatarFallback>
        </Avatar>
        <Box
          className="avatar-overlay"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: "50%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: 0,
            transition: "opacity 0.2s",
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
        </Box>
      </Box>

      {/* Name and Email */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: "0",
        }}
      >
        <Typography
          sx={{ fontSize: "1rem", fontWeight: 600, mb: 0.5 }}
        >
          {profile.first_name} {profile.last_name}
        </Typography>
        <Typography
          sx={{
            color: muiTheme.palette.text.secondary,
            fontSize: "0.875rem",
            wordBreak: "break-word",
          }}
        >
          {profile.email}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {activeTab === "notifications" && canViewMutedSources && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {notificationSubtabsForUser.map((subtab) => (
            <Button
              key={subtab.value}
              variant={activeNotificationSubtab === subtab.value ? "outline" : "ghost"}
              size="sm"
              onClick={() => {
                if (activeNotificationSubtab === subtab.value) return;
                setActiveNotificationSubtab(subtab.value);
                if (subtab.value === "channels") {
                  setPreferencesVersion((prev) => prev + 1);
                } else {
                  setMutedRefreshKey((prev) => prev + 1);
                }
              }}
              sx={getFilterButtonStyles(activeNotificationSubtab === subtab.value, muiTheme, neutrals, colors)}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <subtab.icon size={14} />
                {subtab.label}
              </Box>
            </Button>
          ))}
        </Box>
      )}
      {activeTab === "personal" && (
        <UnsavedChangesBar
          count={profileChanges}
          saving={savingProfile}
          onDiscard={discardProfile}
          onSave={handleSave}
        />
      )}
      {activeTab === "personal" && (
        <Card sx={{ width: "fit-content", minWidth: 0 }}>
          <CardContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {profileHeader}
              {/* Row 1: First Name and Last Name */}
              <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    flex: 1,
                  }}
                >
                  <Label htmlFor="first_name">Nombre</Label>
                  <CustomTextField
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) =>
                      handleInputChange("first_name", e.target.value)
                    }
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (!value || value.trim() === "") {
                        setFieldErrors((prev) => ({
                          ...prev,
                          first_name: true,
                        }));
                      }
                    }}
                    placeholder="Ej: Juan"
                    setValueOnClickAway={false}
                    error={fieldErrors.first_name}
                  />
                  {fieldErrors.first_name && (
                    <Typography
                      sx={{
                        color: "error.main",
                        fontSize: "0.75rem",
                        mt: 0.5,
                      }}
                    >
                      Este campo es obligatorio
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    flex: 1,
                  }}
                >
                  <Label htmlFor="last_name">Apellido</Label>
                  <CustomTextField
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) =>
                      handleInputChange("last_name", e.target.value)
                    }
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (!value || value.trim() === "") {
                        setFieldErrors((prev) => ({
                          ...prev,
                          last_name: true,
                        }));
                      }
                    }}
                    placeholder="Ej: García"
                    setValueOnClickAway={false}
                    error={fieldErrors.last_name}
                  />
                  {fieldErrors.last_name && (
                    <Typography
                      sx={{
                        color: "error.main",
                        fontSize: "0.75rem",
                        mt: 0.5,
                      }}
                    >
                      Este campo es obligatorio
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Row 2: Telephone */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Label htmlFor="telephone">Teléfono</Label>
                <Box sx={{ position: "relative" }}>
                  <Phone
                    size={16}
                    style={{
                      position: "absolute",
                      left: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: muiTheme.palette.text.secondary,
                      zIndex: 1,
                    }}
                  />
                  <CustomTextField
                    id="telephone"
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) =>
                      handleInputChange("telephone", e.target.value)
                    }
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (!value || value.trim() === "") {
                        setFieldErrors((prev) => ({
                          ...prev,
                          telephone: true,
                        }));
                      }
                    }}
                    placeholder="Ej: +34 123 456 789"
                    sx={{ paddingLeft: "40px" }}
                    setValueOnClickAway={false}
                    error={fieldErrors.telephone}
                  />
                </Box>
                {fieldErrors.telephone && (
                  <Typography
                    sx={{ color: "error.main", fontSize: "0.75rem", mt: 0.5 }}
                  >
                    Este campo es obligatorio
                  </Typography>
                )}
              </Box>

              {/* Row 3: Email */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Label htmlFor="email">Correo electrónico</Label>
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-flex",
                    alignItems: "center",
                    width: "fit-content",
                    height: "35px",
                    paddingLeft: "40px",
                    paddingRight: "12px",
                    fontSize: "14px",
                    lineHeight: 1.4,
                    fontFamily: muiTheme.typography.fontFamily,
                    backgroundColor: muiTheme.palette.background.default,
                    color: muiTheme.palette.text.primary,
                    border: `1px solid ${muiTheme.palette.divider}`,
                    borderRadius: "8px",
                    opacity: 0.6,
                    cursor: "not-allowed",
                  }}
                >
                  <Mail
                    size={16}
                    style={{
                      position: "absolute",
                      left: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: muiTheme.palette.text.secondary,
                      zIndex: 1,
                    }}
                  />
                  {profile.email || "Ej: usuario@email.com"}
                </Box>
              </Box>

            </Box>
          </CardContent>
        </Card>
      )}

      {activeTab === "hour-package" && (
        <Box sx={{ width: "100%", maxWidth: 720, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          {!agencyIdForHourPackage ? (
            <Typography variant="body2" color="error">
              No encontramos la agencia asociada a tu usuario.
            </Typography>
          ) : isLoadingAgencyHourPackage ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : agencyHourPackageError ? (
            <Typography variant="body2" color="error">
              No se pudieron cargar las horas de tickets de la agencia.
            </Typography>
          ) : (
            <TimeLogCard
              timeLog={null}
              agencyBalance={agencyHourPackageBalance}
              agencyId={agencyIdForHourPackage}
              inlinePurchase
              showEmptyTimeLogMessage={false}
              showBalanceSeparator={false}
              showBalanceFootnoteMarkers={true}
              onPageBackground
            />
          )}

          {agencyIdForHourPackage &&
            !isLoadingAgencyHourPackage &&
            !agencyHourPackageError && (
              <Box sx={{ mt: 0.5, display: "flex", flexDirection: "column", gap: 0.25 }}>
                {hasAgencyFeature(user, FEATURE_HOUR_PACKAGE) && (
                  <Typography variant="caption" sx={{ color: muiTheme.palette.text.secondary }}>
                    ¹ Las horas base disponibles se resetean cada 6 meses.
                  </Typography>
                )}
                <Typography variant="caption" sx={{ color: muiTheme.palette.text.secondary }}>
                  ² Las horas compradas disponibles se resetean cada 1 de enero.
                </Typography>
              </Box>
            )}
        </Box>
      )}

      {activeTab === "notifications" && (
        <Card
          sx={{
            width: "800px",
            borderRadius: "12px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {!canViewMutedSources || activeNotificationSubtab === "channels"
            ? renderNotificationPreferences()
            : renderMutedSources()}
        </Card>
      )}

      {activeTab === "prompts" &&
        (userRole === "superadmin" || userRole === "agency_admin") && (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {renderPromptConfigurationTab()}
        </Box>
      )}

      {activeTab === "ticket-freelancers" && userRole === "superadmin" && (
        renderTicketFreelancersTab()
      )}

      {activeTab === "clientify" && userRole === "superadmin" && (
        renderClientifyConnectionTab()
      )}

      {activeTab === "seranking" && userRole === "superadmin" && (
        renderSeRankingConnectionTab()
      )}

      {activeTab === "podio" && renderPodioMigrationTab()}

      {activeTab === "csv-exports" && userRole === "superadmin" && (
        <CsvExportsTab enabled={interceptorReady} />
      )}

      {activeTab === "appearance" && (
        <Card sx={{ width: "100%", maxWidth: "900px", minWidth: 0 }}>
          <CardContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>Tamaño de letra</Typography>
                <Typography variant="body2" sx={{ color: muiTheme.palette.text.secondary, fontSize: "0.8125rem" }}>
                  Cómo se muestran los mensajes en el muro global y los hilos de conversación.
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                  <CustomTextField
                    type="text"
                    inputMode="decimal"
                    value={fontSizeInput}
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      // Solo permitir números y punto decimal
                      const cleanedValue = rawValue.replace(/[^0-9.]/g, "");
                      let nextValue = cleanedValue;
                      const separatorMatch = cleanedValue.match(/[.]/);
                      if (separatorMatch) {
                        const separator = separatorMatch[0];
                        const [integerPart, ...rest] = cleanedValue.split(/[.]/);
                        const fractionalPart = rest.join("").replace(/[.]/g, "");
                        nextValue = `${integerPart}${separator}${fractionalPart}`;
                      }

                      const isAllowedInput =
                        nextValue === "" ||
                        nextValue === "1" ||
                        nextValue === "1." ||
                        /^1[0-6](\.\d*)?$/.test(nextValue);

                      if (!isAllowedInput) {
                        return;
                      }

                      setFontSizeInput(nextValue);

                      const parsedValue = Number.parseFloat(nextValue);
                      if (!Number.isNaN(parsedValue)) {
                        const safeValue = Math.min(parsedValue, FONT_SIZE_MAX);
                        setConversationFontSize(safeValue);
                      }
                    }}
                    onBlur={() => {
                      // Al salir del campo, ajustar el valor si está fuera de rango
                      const validation = parseFontSizeInput(fontSizeInput);
                      setConversationFontSize(validation.value);
                      setFontSizeInput(String(validation.value));
                    }}
                    sx={{
                      width: "80px",
                      textAlign: "center",
                      "& input": {
                        textAlign: "center",
                        padding: "8px 6px",
                        MozAppearance: "textfield",
                      },
                      "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                        WebkitAppearance: "none",
                        margin: 0,
                      },
                    }}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newSize = Math.min(FONT_SIZE_MAX, conversationFontSize + 1);
                        setConversationFontSize(newSize);
                        setFontSizeInput(String(newSize));
                      }}
                      sx={{
                        minWidth: "36px",
                        height: "36px",
                        padding: 0,
                        borderRadius: "999px",
                        border: "none",
                        backgroundColor: "transparent",
                        "&:hover": {
                          backgroundColor: muiTheme.palette.action.hover,
                        },
                      }}
                    >
                      <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                        <Typography sx={{ fontSize: "1.1rem", fontWeight: 700 }}>A</Typography>
                        <ChevronUp
                          size={12}
                          style={{ position: "absolute", top: 2, right: 4, color: muiTheme.palette.text.secondary }}
                        />
                      </Box>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newSize = Math.max(FONT_SIZE_MIN, conversationFontSize - 1);
                        setConversationFontSize(newSize);
                        setFontSizeInput(String(newSize));
                      }}
                      sx={{
                        minWidth: "36px",
                        height: "36px",
                        padding: 0,
                        borderRadius: "999px",
                        border: "none",
                        backgroundColor: "transparent",
                        "&:hover": {
                          backgroundColor: muiTheme.palette.action.hover,
                        },
                      }}
                    >
                      <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>A</Typography>
                        <ChevronDown
                          size={12}
                          style={{ position: "absolute", top: 2, right: 4, color: muiTheme.palette.text.secondary }}
                        />
                      </Box>
                    </Button>
                  </Box>

                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: muiTheme.palette.text.secondary,
                      fontStyle: "italic",
                    }}
                  >
                    ({FONT_SIZE_MIN}-{FONT_SIZE_MAX}px)
                  </Typography>
                </Box>
              </Box>

              {userRole === "agency_admin" ? (
                <AgencyBrandingSettings
                  agencyId={user?.agencyId}
                  fontSize={conversationFontSize}
                  extraDirty={fontSizeDirty}
                  onSaveExtra={saveFontSize}
                  onDiscardExtra={discardFontSize}
                  floatingSave
                />
              ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>Vista previa</Typography>
                  <BrandPreview
                    primary={user?.agencyBranding?.primary_color}
                    secondary={user?.agencyBranding?.secondary_color}
                    logoSrc={user?.agencyBranding?.logo_url || undefined}
                    fontSize={conversationFontSize}
                  />
                  <UnsavedChangesBar
                    count={fontSizeDirty ? 1 : 0}
                    saving={savingFontSize}
                    onDiscard={discardFontSize}
                    onSave={saveFontSize}
                  />
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Photo edit modal */}
      <UnifiedModal
        open={showEditPhotoModal}
        onClose={() => {
          setShowEditPhotoModal(false);
          setSelectedFile(null);
          setPhotoError(null);
          setCropMessage(null);
          if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
          }
        }}
        title={
          profile.profile_image
            ? "Editar imagen de perfil"
            : "Nueva imagen de perfil"
        }
        maxWidth="xs"
      >
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              position: "relative",
            }}
          >
            {displayedImage ? (
              <Box sx={{ position: "relative" }}>
                <Avatar
                  sx={{ width: "80px", height: "80px" }}
                  src={displayedImage}
                >
                  <AvatarFallback
                    sx={{
                      backgroundColor: muiTheme.palette.primary.main,
                      color: muiTheme.palette.primary.contrastText,
                      fontSize: "2rem",
                      fontWeight: 600,
                    }}
                  >
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setImageMarkedForDeletion(true);
                    setSelectedFile(null);
                    if (previewUrl) {
                      URL.revokeObjectURL(previewUrl);
                      setPreviewUrl(null);
                    }
                    setPhotoError(null);
                  }}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    backgroundColor: muiTheme.palette.background.paper,
                    border: `1px solid ${muiTheme.palette.divider}`,
                    width: 28,
                    height: 28,
                    "&:hover": {
                      backgroundColor: muiTheme.palette.action.hover,
                    },
                  }}
                >
                  <X size={14} />
                </IconButton>
              </Box>
            ) : (
              <Box
                onClick={() => fileInputRef.current.click()}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    border: `1px dashed ${muiTheme.palette.divider}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: muiTheme.palette.background.paper,
                  }}
                >
                  <Plus size={28} color={muiTheme.palette.text.secondary} />
                </Box>
                <Typography
                  variant="caption"
                  sx={{ mt: 1, color: muiTheme.palette.text.secondary }}
                >
                  Añadir imagen
                </Typography>
              </Box>
            )}
          </Box>
          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              color: muiTheme.palette.text.secondary,
            }}
          >
            {profile.profile_image
              ? "Edita tu imagen de perfil"
              : "Añade una nueva imagen de perfil"}
          </Typography>
          {photoError && (
            <Typography
              variant="body2"
              color="error"
              sx={{ textAlign: "center" }}
            >
              {photoError}
            </Typography>
          )}
          {cropMessage && (
            <Typography
              variant="body2"
              color="warning"
              sx={{ textAlign: "center" }}
            >
              {cropMessage}
            </Typography>
          )}
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileSelect}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: "auto",
            }}
          >
            <Button
              onClick={() => {
                setShowEditPhotoModal(false);
                setSelectedFile(null);
                setPhotoError(null);
                setCropMessage(null);
                setImageMarkedForDeletion(false);
                if (previewUrl) {
                  URL.revokeObjectURL(previewUrl);
                  setPreviewUrl(null);
                }
              }}
              disabled={uploading}
              variant="outline"
            >
              Cancelar
            </Button>
            <Button
              onClick={async () => {
                if (imageMarkedForDeletion) {
                  await handleDeleteImage();
                } else if (selectedFile) {
                  await handleUpload();
                } else {
                  setShowEditPhotoModal(false);
                  setImageMarkedForDeletion(false);
                }
                setSelectedFile(null);
                setPhotoError(null);
                setCropMessage(null);
                if (previewUrl) {
                  URL.revokeObjectURL(previewUrl);
                  setPreviewUrl(null);
                }
                setImageMarkedForDeletion(false);
              }}
              disabled={uploading}
            >
              {uploading ? "Procesando..." : "Confirmar"}
            </Button>
          </Box>
        </Box>
      </UnifiedModal>

      {/* Delete Clientify Connection Modal */}
      <ConfirmModal
        open={showDeleteConnectionModal}
        onClose={() => setShowDeleteConnectionModal(false)}
        onConfirm={handleConfirmDeleteConnection}
        title="Eliminar conexión"
        question="¿Estás seguro de que quieres eliminar esta conexión?"
        description={
          <>
            Se eliminará la conexión entre{" "}
            <strong>{connectionToDelete?.user_name || connectionToDelete?.user_email}</strong> y el
            usuario de Clientify{" "}
            <strong>
              {clientifyUserLabelById.get(String(connectionToDelete?.clientify_user_id)) ||
                connectionToDelete?.clientify_user_id}
            </strong>
            . Dejará de ver sus leads de Clientify hasta que se vuelva a conectar.
          </>
        }
        confirmLabel="Eliminar conexión"
        loadingLabel="Eliminando..."
        loading={deletingConnection}
        tone="danger"
      />

      {/* Delete SE Ranking Connection Modal */}
      <ConfirmModal
        open={showDeleteSerankingModal}
        onClose={() => setShowDeleteSerankingModal(false)}
        onConfirm={handleConfirmDeleteSerankingConnection}
        title="Eliminar conexión"
        question="¿Estás seguro de que quieres eliminar esta conexión?"
        description={
          <>
            Se eliminará la conexión entre{" "}
            <strong>
              {serankingConnectionToDelete?.user_name ||
                serankingConnectionToDelete?.user_email ||
                "este usuario"}
            </strong>{" "}
            y la subcuenta de SE Ranking{" "}
            <strong>
              {serankingConnectionToDelete?.seranking_email ||
                serankingConnectionToDelete?.seranking_user_id}
            </strong>
            . Esta acción se puede revertir creando una nueva conexión.
          </>
        }
        confirmLabel="Eliminar conexión"
        loadingLabel="Eliminando..."
        loading={deletingSerankingConnection}
        tone="danger"
      />

      {/* Success/Error Popup */}
      <SuccessPopup
        message={popupMessage}
        isOpen={showSuccessPopup}
        onClose={() => setShowSuccessPopup(false)}
        autoHideDuration={popupDuration}
        error={popupError}
      />
    </Box>
  );
}

export default UserSettings;
